// @ts-nocheck - Los errores de JSX.IntrinsicElements son falsos positivos del linter
// El código compila y funciona correctamente con React 18
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import apiService from '../../services/api.service'
import { getAssignedFincaId, getCurrentUser, getUserData, isAdminFinca } from '../../services/auth.service'
import LoadingOverlay from '../../components/loading/LoadingOverlay'
import { Loader } from '../../components/loading'
import CustomDropdown from '../../components/dropdown'
import {
  TIPO_RECLASIFICACION_RESTA,
  TIPO_RECLASIFICACION_SUMA,
  TIPO_RECLASIFICACION_UI,
  descripcionTipoMovimiento,
  subtituloEfectoTipoMovimiento,
  ocultarEnSelectorTipoMovimiento,
  esTipoMovimientoSoloSelectorUi,
} from '../../constants/inventarioTiposMovimiento'
import './inventario_finca.css'

/**
 * Política vista Inventario Finca (independiente de inventario_ganado):
 * - Valor monetario del ganado: no se muestra ni se envía en esta vista.
 * - Peso: desactivado por ahora; activar INVENTARIO_FINCA_PERMITE_PESO cuando el rol deba registrarlo.
 * Los flags se conectarán más adelante con roles/permisos reales.
 */
const INVENTARIO_FINCA_PERMITE_VALOR = false
const INVENTARIO_FINCA_PERMITE_PESO = false

function sanitizarResumenInventarioFinca(resumen: ResumenHierro[]): ResumenHierro[] {
  if (INVENTARIO_FINCA_PERMITE_VALOR && INVENTARIO_FINCA_PERMITE_PESO) return resumen
  return resumen.map((h) => {
    const categorias = h.categorias.map((c) => {
      const copia: ResumenCategoria = { ...c }
      if (!INVENTARIO_FINCA_PERMITE_VALOR) delete (copia as Partial<ResumenCategoria>).valor_total
      if (!INVENTARIO_FINCA_PERMITE_PESO) delete (copia as Partial<ResumenCategoria>).peso_total
      return copia
    })
    const hCopia: ResumenHierro = { ...h, categorias }
    if (!INVENTARIO_FINCA_PERMITE_VALOR) delete (hCopia as Partial<ResumenHierro>).valor_total
    if (!INVENTARIO_FINCA_PERMITE_PESO) delete (hCopia as Partial<ResumenHierro>).peso_total
    return hCopia
  })
}

function sanitizarMovimientosInventarioFinca(movimientos: Movimiento[]): Movimiento[] {
  if (INVENTARIO_FINCA_PERMITE_VALOR && INVENTARIO_FINCA_PERMITE_PESO) return movimientos
  return movimientos.map((m) => {
    const copia = { ...m } as Movimiento & { peso?: number; valor?: number }
    if (!INVENTARIO_FINCA_PERMITE_VALOR) delete copia.valor
    if (!INVENTARIO_FINCA_PERMITE_PESO) delete copia.peso
    return copia as Movimiento
  })
}

// Interfaces para los tipos de datos
interface Categoria {
  codigo: string;
  nombre: string;
}

interface TipoMovimiento {
  tipo: string;
  efecto: 'suma' | 'resta' | 'reclasificacion';
  descripcion: string;
}

interface Movimiento {
  _id?: string;
  movimiento_id?: string;
  libro: string;
  libro_nombre?: string;
  libro_id?: string;
  hoja: number;
  fecha: string;
  hierro: string;
  hierro_id?: string;
  finca_id?: string;
  categoria: string;
  tipo_movimiento: string;
  cantidad: number;
  usuario: string;
  observaciones?: string;
  created_at: string;
}

interface ResumenCategoria {
  codigo: string;
  nombre: string;
  total: number;
  valor_total?: number;
  peso_total?: number;
  movimientos: number;
}

interface ResumenHierro {
  hierro: string;
  hierro_id: string;
  finca_id: string;
  finca_nombre: string;
  categorias: ResumenCategoria[];
  total_general: number;
  valor_total?: number;
  peso_total?: number;
  // Nota: El resumen puede mostrar múltiples entradas si un hierro está en varias fincas
}

interface CategoriasResponse {
  categorias: Categoria[];
  total: number;
}

interface TiposMovimientoResponse {
  tipos_movimiento: TipoMovimiento[];
  total: number;
}

interface MovimientosResponse {
  movimientos: Movimiento[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  filters_applied: {
    hierro?: string | null;
    hierro_id?: string | null;
    finca_id?: string | null;
    categoria?: string | null;
    tipo_movimiento?: string | null;
    fecha_inicio?: string | null;
    fecha_fin?: string | null;
    libro_id?: string | null;
  };
}

interface LibroInventario {
  libro_id: string;
  nombre: string;
  finca_id: string;
  finca_nombre?: string;
  created_at?: string;
}

interface LibrosInventarioResponse {
  libros: LibroInventario[];
  total: number;
  finca_id: string;
}

interface MovimientoEditableForm {
  fecha: string;
  hierro: string;
  categoria: string;
  tipo_movimiento: string;
  cantidad: string;
  libro_id: string;
  hoja: string;
  observaciones: string;
}

interface ResumenResponse {
  resumen: ResumenHierro[];
  filtros_aplicados: {
    hierro: string | null;
    finca_id: string | null;
  };
  total_combinaciones: number;
}

interface FincaAsignada {
  finca_id: string;
  finca_nombre: string;
  fecha_asignacion: string;
  asignado_por: string;
}

interface Hierro {
  _id: string;
  nombre: string;
  fincas_asignadas: FincaAsignada[];
  imagen?: string;
  imagen_url?: string;
  dueno: string;
  numero_documento: string;
  fecha_ingreso: string;
  descripcion?: string;
  created_at: string;
  updated_at: string;
  creado_por?: string;
  actualizado_por?: string;
  deleted_at?: string | null;
  deleted_by?: string | null;
}

interface HierrosResponse {
  hierros: Hierro[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  filters_applied: {
    finca_id?: string | null;
    nombre?: string | null;
  };
}

interface Finca {
  _id: string;
  nombre: string;
  codigo: string;
  tipos: string[];
  imagen?: string;
  imagen_url?: string;
}

interface FincasResponse {
  fincas: Finca[];
  total: number;
}

const ORDEN_CATEGORIAS_MOVIMIENTOS: string[][] = [
  ['VP', 'VACA PARIDA'],
  ['CM', 'CRIA MACHO'],
  ['CH', 'CRIA HEMBRA'],
  ['VHP', 'VACA HORRA PRENADA'],
  ['V. PROD VACIA', 'VACA PRODUCCION VACIA'],
  ['V. PROD PREN', 'VACA PRODUCCION PRENADA'],
  ['N.V PREN', 'NOVILLA DE VIENTE PRENADA'],
  ['V.TRE PROG', 'VIENTRE PROGRAMADO'],
  ['HL', 'HEMBRA DE LEVANTE'],
  ['NVV', 'NOVILLA VIENTRE VACIA'],
  ['VHV', 'VACA HORRA VACIA'],
  ['ML', 'MACHO DE LEVANTE'],
  ['MC', 'MACHO CEBA'],
  ['TRE', 'TORETE'],
  ['TRO', 'TORO'],
  ['BFL', 'BUFALO'],
];

const normalizarCategoriaMovimiento = (valor?: string): string =>
  (valor || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\./g, '')
    .replace(/\s+/g, '')
    .toUpperCase();

const ORDEN_CATEGORIAS_MAP = ORDEN_CATEGORIAS_MOVIMIENTOS.reduce<Record<string, number>>((acc, aliases, index) => {
  aliases.forEach((alias) => {
    acc[normalizarCategoriaMovimiento(alias)] = index;
  });
  return acc;
}, {});

const getOrdenCategoriaMovimiento = (categoria?: string): number => {
  const normalized = normalizarCategoriaMovimiento(categoria);
  return ORDEN_CATEGORIAS_MAP[normalized] ?? Number.MAX_SAFE_INTEGER;
};

const getBloqueMovimientoKey = (mov: Movimiento): string =>
  [
    mov.created_at || '',
    mov.fecha || '',
    mov.hierro_id || mov.hierro || '',
    mov.libro_id || mov.libro || '',
    mov.hoja ?? '',
    mov.tipo_movimiento || '',
    mov.usuario || '',
  ].join('|');

function ordenarMovimientosPorCategoriaCreacion(movimientos: Movimiento[]): Movimiento[] {
  return [...movimientos].sort((a, b) => {
    if (getBloqueMovimientoKey(a) !== getBloqueMovimientoKey(b)) return 0;
    return getOrdenCategoriaMovimiento(a.categoria) - getOrdenCategoriaMovimiento(b.categoria);
  });
}

const InventarioFinca = (): JSX.Element => {
  // Estados principales
  const [activeTab, setActiveTab] = useState<'resumen' | 'movimientos' | 'nuevo'>('resumen');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Estados para Hierros
  const [hierros, setHierros] = useState<Hierro[]>([]);
  const [hierroSeleccionado, setHierroSeleccionado] = useState<string>('');
  const [fincaFiltro, setFincaFiltro] = useState<string>('');
  const [mostrarModalHierro, setMostrarModalHierro] = useState<boolean>(false);
  const [nuevoHierro, setNuevoHierro] = useState({
    nombre: '',
    finca_ids: [] as string[],
    dueno: '',
    numero_documento: '',
    fecha_ingreso: new Date().toISOString().split('T')[0],
    descripcion: ''
  });

  // Estados para Fincas
  const [fincas, setFincas] = useState<Finca[]>([]);

  const opcionesFiltroFinca = useMemo(() => {
    const mapped = fincas.map((finca) => ({ value: finca._id, label: finca.nombre }));
    if (isAdminFinca()) return mapped;
    return [{ value: '', label: 'Todas las fincas' }, ...mapped];
  }, [fincas]);

  const placeholderFincaFiltro = isAdminFinca() ? 'Seleccione finca' : 'Todas las fincas';

  // Estados para imagen del hierro
  const [imagenHierro, setImagenHierro] = useState<File | null>(null);
  const [previewImagen, setPreviewImagen] = useState<string | null>(null);

  // Función helper para construir la URL completa de la imagen
  const getImagenUrl = (imagenUrl?: string, imagen?: string): string | null => {
    if (!imagenUrl && !imagen) return null;
    
    const url = imagenUrl || imagen || '';
    
    // Si ya es una URL completa (http:// o https://), devolverla tal cual
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // Si es una ruta relativa, construir la URL completa con el baseURL
    const baseURL = process.env.REACT_APP_API_URL || '';
    
    // Normalizar las barras: remover barra final del baseURL y barra inicial de la URL si existe
    const normalizedBaseURL = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
    const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
    
    return `${normalizedBaseURL}${normalizedUrl}`;
  };

  // Estados para Resumen
  const [resumen, setResumen] = useState<ResumenHierro[]>([]);

  const totalesInventarioFincaResumen = useMemo(() => {
    if (!fincaFiltro || hierroSeleccionado) return null;
    let cantidad = 0;
    let valor = 0;
    let peso = 0;
    for (const r of resumen) {
      cantidad += Number(r.total_general) || 0;
      valor += Number(r.valor_total) || 0;
      peso += Number(r.peso_total) || 0;
    }
    return { cantidad, valor, peso };
  }, [fincaFiltro, hierroSeleccionado, resumen]);

  // Estados para Movimientos
  const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
  const [filtrosMovimientos, setFiltrosMovimientos] = useState({
    categoria: '',
    tipo_movimiento: '',
    libro_id: '',
    fecha_inicio: '',
    fecha_fin: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 50,
    total: 0,
    total_pages: 0
  });
  const [edicionMovimientosHabilitada, setEdicionMovimientosHabilitada] = useState<boolean>(false);
  const [movimientosEditables, setMovimientosEditables] = useState<Record<string, MovimientoEditableForm>>({});
  const [guardandoMovimientoId, setGuardandoMovimientoId] = useState<string | null>(null);
  const [dropdownEdicionActivo, setDropdownEdicionActivo] = useState<string | null>(null);
  const dropdownEdicionRef = useRef<HTMLDivElement | null>(null);
  const [dropdownFiltrosActivo, setDropdownFiltrosActivo] = useState<string | null>(null);
  const dropdownFiltrosRef = useRef<HTMLDivElement | null>(null);
  const [dropdownFincaActivo, setDropdownFincaActivo] = useState<string | null>(null);
  const dropdownFincaRef = useRef<HTMLDivElement | null>(null);

  // Estados para Nuevo Movimiento
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [tiposMovimiento, setTiposMovimiento] = useState<TipoMovimiento[]>([]);
  const [librosInventario, setLibrosInventario] = useState<LibroInventario[]>([]);
  const [loadingLibros, setLoadingLibros] = useState<boolean>(false);
  const [mostrarDropdownLibro, setMostrarDropdownLibro] = useState<boolean>(false);
  const [mostrarNuevoLibro, setMostrarNuevoLibro] = useState<boolean>(false);
  const [nuevoLibroNombre, setNuevoLibroNombre] = useState<string>('');
  const [guardandoLibro, setGuardandoLibro] = useState<boolean>(false);
  const libroDropdownRef = useRef<HTMLDivElement | null>(null);
  const [nuevoMovimiento, setNuevoMovimiento] = useState({
    libro_id: '',
    hoja: '',
    fecha: new Date().toISOString().split('T')[0],
    hierro: '',
    categoria: '',
    tipo_movimiento: '',
    cantidad: ''
  });

  // Estados para modales de selección de categoría
  const [mostrarModalTipoMovimiento, setMostrarModalTipoMovimiento] = useState<boolean>(false);
  const [mostrarModalCantidad, setMostrarModalCantidad] = useState<boolean>(false);
  const [mostrarModalPeso, setMostrarModalPeso] = useState<boolean>(false);
  const [mostrarModalValor, setMostrarModalValor] = useState<boolean>(false);
  const [mostrarModalCategoriaDestino, setMostrarModalCategoriaDestino] = useState<boolean>(false);
  const [categoriaSeleccionadaModal, setCategoriaSeleccionadaModal] = useState<Categoria | null>(null);
  const [categoriaDestinoReclasificacion, setCategoriaDestinoReclasificacion] = useState<Categoria | null>(null);
  const [tipoMovimientoSeleccionadoModal, setTipoMovimientoSeleccionadoModal] = useState<TipoMovimiento | null>(null);
  const [cantidadTemporal, setCantidadTemporal] = useState<string>('');
  const [pesoTemporal, setPesoTemporal] = useState<string>('');
  const [valorTemporal, setValorTemporal] = useState<string>('');

  // Estado para almacenar las selecciones de categorías con sus tipos, cantidades y pesos
  interface CategoriaSeleccionada {
    codigo: string;
    nombre: string;
    tipo_movimiento: string;
    tipo_movimiento_descripcion: string;
    cantidad: number;
    peso?: number;
    valor?: number;
    observaciones?: string;
    mostrarObservacion?: boolean;
    categoria_destino?: string; // Para reclasificación: categoría destino
    categoria_origen?: string; // Para reclasificación: categoría origen
  }
  const [categoriasSeleccionadas, setCategoriasSeleccionadas] = useState<CategoriaSeleccionada[]>([]);

  // Cargar fincas, categorías y tipos de movimiento al inicio
  useEffect(() => {
    loadFincas();
    loadCategorias();
    loadTiposMovimiento();
  }, []);

  // Cargar hierros cuando cambia el filtro de finca (o todos si no hay finca seleccionada)
  useEffect(() => {
    // Cargar todos los hierros si no hay finca seleccionada, o solo los de la finca si hay una seleccionada
    loadHierros(fincaFiltro || undefined);
  }, [fincaFiltro]);

  // Cargar datos según el tab activo - permite ver todos, por finca o por hierro
  useEffect(() => {
    if (activeTab === 'resumen') {
      loadResumen();
    }
  }, [activeTab, hierroSeleccionado, fincaFiltro]);

  // Cargar movimientos cuando cambian los filtros, paginación o el hierro seleccionado (o todos si no hay hierro seleccionado pero hay finca)
  useEffect(() => {
    if (activeTab === 'movimientos' && fincaFiltro) {
      loadMovimientos();
    }
  }, [filtrosMovimientos, pagination.page, hierroSeleccionado, activeTab, fincaFiltro]);

  useEffect(() => {
    if (edicionMovimientosHabilitada) {
      inicializarEdicionMovimientos();
    }
  }, [movimientos, edicionMovimientosHabilitada]);

  useEffect(() => {
    if (fincaFiltro) {
      loadLibrosInventario(fincaFiltro);
    } else {
      setLibrosInventario([]);
      setNuevoMovimiento(prev => ({ ...prev, libro_id: '' }));
    }
    setMostrarDropdownLibro(false);
  }, [fincaFiltro]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (libroDropdownRef.current && !libroDropdownRef.current.contains(target)) {
        setMostrarDropdownLibro(false);
      }
    };

    if (mostrarDropdownLibro) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mostrarDropdownLibro]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (dropdownEdicionRef.current && !dropdownEdicionRef.current.contains(target)) {
        setDropdownEdicionActivo(null);
      }
    };

    if (dropdownEdicionActivo) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownEdicionActivo]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (dropdownFiltrosRef.current && !dropdownFiltrosRef.current.contains(target)) {
        setDropdownFiltrosActivo(null);
      }
    };

    if (dropdownFiltrosActivo) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownFiltrosActivo]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (dropdownFincaRef.current && !dropdownFincaRef.current.contains(target)) {
        setDropdownFincaActivo(null);
      }
    };

    if (dropdownFincaActivo) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownFincaActivo]);

  useEffect(() => {
    if (!isAdminFinca()) return;
    let cancelled = false;
    getUserData()
      .then((data) => {
        const fid = data.user.finca_id;
        if (cancelled || !fid) return;
        setFincaFiltro((prev) => (prev === '' ? fid : prev));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAdminFinca() || fincas.length === 0) return;
    setFincaFiltro((prev) => {
      if (prev !== '') return prev;
      const fromUser = getAssignedFincaId();
      if (fromUser && fincas.some((f) => f._id === fromUser)) return fromUser;
      if (fincas.length === 1) return fincas[0]._id;
      return prev;
    });
  }, [fincas]);

  const loadFincas = async (): Promise<void> => {
    try {
      const response = await apiService.get<FincasResponse>('/api/fincas');
      setFincas(response.data.fincas);
    } catch (err: any) {
      console.error('Error al cargar fincas:', err);
      setError('Error al cargar fincas');
    }
  };

  const getMovimientoId = (mov: Movimiento): string => mov.movimiento_id || mov._id || '';
  const getMovimientoKey = (mov: Movimiento, index: number): string => {
    const movimientoId = getMovimientoId(mov);
    if (movimientoId) return movimientoId;
    return `${mov.libro_id || mov.libro || 'sin-libro'}-${mov.hoja}-${mov.fecha}-${index}`;
  };

  const getFechaInputValue = (fecha: string): string => {
    if (!fecha) return '';
    if (fecha.includes('T')) return fecha.split('T')[0];
    const date = new Date(fecha);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const mapMovimientoToEditable = (mov: Movimiento): MovimientoEditableForm => ({
    fecha: getFechaInputValue(mov.fecha),
    hierro: mov.hierro || '',
    categoria: mov.categoria || '',
    tipo_movimiento: mov.tipo_movimiento || '',
    cantidad: mov.cantidad !== undefined && mov.cantidad !== null ? String(mov.cantidad) : '',
    libro_id: mov.libro_id || '',
    hoja: mov.hoja !== undefined && mov.hoja !== null ? String(mov.hoja) : '',
    observaciones: mov.observaciones || ''
  });

  const inicializarEdicionMovimientos = (): void => {
    const editables: Record<string, MovimientoEditableForm> = {};
    movimientos.forEach((mov, index) => {
      const movimientoKey = getMovimientoKey(mov, index);
      editables[movimientoKey] = mapMovimientoToEditable(mov);
    });
    setMovimientosEditables(editables);
  };

  const handleHabilitarEdicionMovimientos = (): void => {
    const password = window.prompt('Para habilitar la edición de movimientos, ingrese su clave de usuario:');
    if (!password || password.trim() === '') {
      setError('Se requiere clave para habilitar la edición de movimientos.');
      return;
    }

    setError(null);
    setSuccess('Edición de movimientos habilitada.');
    setEdicionMovimientosHabilitada(true);
    inicializarEdicionMovimientos();
  };

  const handleCancelarEdicionMovimientos = (): void => {
    setEdicionMovimientosHabilitada(false);
    setMovimientosEditables({});
    setGuardandoMovimientoId(null);
  };

  const handleEditableFieldChange = (movimientoId: string, field: keyof MovimientoEditableForm, value: string): void => {
    setMovimientosEditables((prev) => ({
      ...prev,
      [movimientoId]: {
        ...prev[movimientoId],
        [field]: value
      }
    }));
  };

  const handleResetMovimientoEditable = (mov: Movimiento, index: number): void => {
    const movimientoId = getMovimientoKey(mov, index);
    setMovimientosEditables((prev) => ({
      ...prev,
      [movimientoId]: mapMovimientoToEditable(mov)
    }));
  };

  const buildMovimientoPatchPayload = (
    original: Movimiento,
    editable: MovimientoEditableForm
  ): Record<string, unknown> => {
    const payload: Record<string, unknown> = {};
    const fechaOriginal = getFechaInputValue(original.fecha);
    if (editable.fecha && editable.fecha !== fechaOriginal) payload.fecha = editable.fecha;

    const hierroOriginal = (original.hierro || '').trim();
    const hierroEditado = (editable.hierro || '').trim();
    if (hierroEditado !== hierroOriginal) {
      const hierroEncontrado = hierros.find((h) => h.nombre.toLowerCase() === hierroEditado.toLowerCase());
      if (!hierroEncontrado) {
        throw new Error('El hierro ingresado no coincide con un hierro existente.');
      }
      payload.hierro_id = hierroEncontrado._id;
    }

    if (editable.categoria && editable.categoria !== original.categoria) payload.categoria = editable.categoria;
    if (editable.tipo_movimiento && editable.tipo_movimiento !== original.tipo_movimiento) payload.tipo_movimiento = editable.tipo_movimiento;

    const cantidadNumerica = Number.parseInt(editable.cantidad, 10);
    if (Number.isNaN(cantidadNumerica) || cantidadNumerica <= 0) {
      throw new Error('La cantidad debe ser mayor a 0.');
    }
    if (cantidadNumerica !== Number(original.cantidad)) payload.cantidad = cantidadNumerica;

    const hojaNumerica = Number.parseInt(editable.hoja, 10);
    if (Number.isNaN(hojaNumerica) || hojaNumerica <= 0) {
      throw new Error('La hoja debe ser mayor a 0.');
    }
    if (hojaNumerica !== Number(original.hoja)) payload.hoja = hojaNumerica;

    const libroOriginal = original.libro_id || '';
    if (editable.libro_id && editable.libro_id !== libroOriginal) payload.libro_id = editable.libro_id;

    const observacionOriginal = original.observaciones || '';
    const observacionEditada = editable.observaciones.trim();
    if (observacionEditada !== observacionOriginal) {
      payload.observaciones = observacionEditada || null;
    }

    return payload;
  };

  const handleGuardarMovimientoEditado = async (mov: Movimiento, index: number): Promise<void> => {
    const movimientoId = getMovimientoId(mov);
    if (!movimientoId) {
      setError('No se pudo editar: este movimiento no incluye movimiento_id. Recargue la lista e intente de nuevo.');
      return;
    }
    const movimientoKey = getMovimientoKey(mov, index);
    const editable = movimientosEditables[movimientoKey];
    if (!editable) {
      setError('No se encontró estado editable para este movimiento.');
      return;
    }

    try {
      setGuardandoMovimientoId(movimientoKey);
      setError(null);
      setSuccess(null);

      const payload = buildMovimientoPatchPayload(mov, editable);
      if (Object.keys(payload).length === 0) {
        setError('No se detectaron cambios para guardar.');
        return;
      }

      await apiService.patch(`/api/inventario/movimientos/${movimientoId}`, payload);
      setSuccess('Movimiento actualizado correctamente.');
      await loadMovimientos();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Error al actualizar el movimiento');
    } finally {
      setGuardandoMovimientoId(null);
    }
  };

  const loadHierros = async (fincaId?: string): Promise<void> => {
    try {
      const params: any = {
        per_page: 100 // Cargar todos los hierros
      };
      
      // Si hay un filtro de finca, agregarlo
      if (fincaId) {
        params.finca_id = fincaId;
      }
      
      const response = await apiService.get<HierrosResponse>('/api/hierros', { params });
      let hierrosConImagenes = response.data.hierros;
      
      // Filtrar hierros eliminados (soft delete) - excluir los que tienen deleted_at
      hierrosConImagenes = hierrosConImagenes.filter((h: Hierro) => !h.deleted_at);
      
      // Para cada hierro que no tenga imagen_url, obtener los detalles completos del endpoint individual
      const hierrosSinImagen = hierrosConImagenes.filter((h: Hierro) => !h.imagen_url && !h.imagen);
      
      if (hierrosSinImagen.length > 0) {
        // Obtener imágenes para los hierros que no las tienen
        const promesasImagenes = hierrosSinImagen.map(async (hierro: Hierro) => {
          try {
            const detalleResponse = await apiService.get<Hierro>(`/api/hierros/${hierro._id}`);
            return {
              id: hierro._id,
              imagen_url: detalleResponse.data.imagen_url,
              imagen: detalleResponse.data.imagen
            };
          } catch (err) {
            console.error(`Error al obtener imagen del hierro ${hierro._id}:`, err);
            return null;
          }
        });
        
        const imagenesObtenidas = await Promise.all(promesasImagenes);
        
        // Actualizar los hierros con las imágenes obtenidas
        hierrosConImagenes = hierrosConImagenes.map((hierro: Hierro) => {
          const imagenData = imagenesObtenidas.find((img: { id: string; imagen_url?: string; imagen?: string } | null) => img && img.id === hierro._id);
          if (imagenData) {
            return {
              ...hierro,
              imagen_url: imagenData.imagen_url,
              imagen: imagenData.imagen
            };
          }
          return hierro;
        });
      }
      
      setHierros(hierrosConImagenes);
      
      // Mantener "Todos" (sin hierro seleccionado) para que el resumen total por finca se muestre.
      setHierroSeleccionado(prev => {
        if (hierrosConImagenes.length === 0) return '';
        if (!prev) return '';
        if (!hierrosConImagenes.find((h: Hierro) => h._id === prev)) return '';
        return prev;
      });
    } catch (err: any) {
      console.error('Error al cargar hierros:', err);
      setError('Error al cargar hierros');
    }
  };

  const handleFincaToggle = (fincaId: string): void => {
    setNuevoHierro(prev => {
      const fincaIds = prev.finca_ids.includes(fincaId)
        ? prev.finca_ids.filter(id => id !== fincaId)
        : [...prev.finca_ids, fincaId];
      return { ...prev, finca_ids: fincaIds };
    });
  };

  const handleImagenChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar formato de archivo
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        setError('Tipo de archivo no permitido. Se permiten: PNG, JPG, JPEG, GIF');
        return;
      }
      
      // Validar tamaño (opcional, por ejemplo máximo 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setError('El archivo es demasiado grande. Tamaño máximo: 5MB');
        return;
      }
      
      setImagenHierro(file);
      setError(null); // Limpiar error si la validación pasa
      
      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImagen(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Función helper para obtener nombres de fincas asignadas
  const getFincasNombres = (hierro: Hierro): string => {
    if (!hierro.fincas_asignadas || hierro.fincas_asignadas.length === 0) {
      return 'Sin finca';
    }
    if (hierro.fincas_asignadas.length === 1) {
      return hierro.fincas_asignadas[0].finca_nombre;
    }
    return hierro.fincas_asignadas.map(f => f.finca_nombre).join(', ');
  };

  // Función helper para verificar si un hierro está asignado a una finca específica
  const hierroEstaEnFinca = (hierro: Hierro, fincaId: string): boolean => {
    return hierro.fincas_asignadas?.some(f => f.finca_id === fincaId) || false;
  };

  const handleCrearHierro = async (e: React.SyntheticEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    // Validar que se haya seleccionado al menos una finca
    if (nuevoHierro.finca_ids.length === 0) {
      setError('Debe seleccionar al menos una finca');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Crear un solo hierro con múltiples fincas asignadas
      const hierroData = {
        nombre: nuevoHierro.nombre,
        fincas_ids: nuevoHierro.finca_ids, // Ahora es un array
        dueno: nuevoHierro.dueno,
        numero_documento: nuevoHierro.numero_documento,
        fecha_ingreso: nuevoHierro.fecha_ingreso,
        descripcion: nuevoHierro.descripcion || undefined
      };

      const response = await apiService.post<{ 
        message: string; 
        hierro: Hierro;
        asignaciones_realizadas?: {
          exitosas: number;
          fallidas: number;
          detalles?: Array<{ finca_id: string; error?: string }>;
        };
      }>('/api/hierros', hierroData);
      
      const hierroCreado = response.data.hierro;
      
      // Agregar el hierro a la lista
      setHierros(prev => [...prev, hierroCreado]);
      
      // Si hay una imagen, subirla
      if (imagenHierro) {
        try {
          const formData = new FormData();
          formData.append('hierro_id', hierroCreado._id);
          formData.append('image', imagenHierro);
          
          const imagenResponse = await apiService.post<{ message: string; imagen_url: string }>('/api/hierros/imagen', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          // Actualizar el hierro en la lista con la imagen
          setHierros(prev => prev.map(h => 
            h._id === hierroCreado._id 
              ? { ...h, imagen_url: imagenResponse.data.imagen_url, imagen: imagenResponse.data.imagen_url.split('/').pop() || undefined }
              : h
          ));
        } catch (imgErr: any) {
          console.error('Error al subir imagen:', imgErr);
          setError('Hierro creado correctamente, pero hubo un error al subir la imagen: ' + (imgErr.response?.data?.error || 'Error desconocido'));
        }
      }

      // Mostrar mensajes de éxito o error
      const asignaciones = response.data.asignaciones_realizadas;
      if (asignaciones) {
        if (asignaciones.exitosas > 0) {
          if (asignaciones.fallidas === 0) {
            setSuccess(`Hierro creado correctamente y asignado a ${asignaciones.exitosas} finca(s)` + (imagenHierro ? ' con imagen' : ''));
          } else {
            setSuccess(`Hierro creado correctamente. ${asignaciones.exitosas} asignación(es) exitosa(s), ${asignaciones.fallidas} fallida(s)`);
            if (asignaciones.detalles) {
              const erroresDetalle = asignaciones.detalles
                .filter((d: { finca_id: string; error?: string }) => d.error)
                .map((d: { finca_id: string; error?: string }) => d.error)
                .join('; ');
              if (erroresDetalle) {
                setError(erroresDetalle);
              }
            }
          }
        } else {
          setError('Hierro creado pero no se pudo asignar a ninguna finca. Verifique que las fincas existan.');
        }
      } else {
        setSuccess('Hierro creado correctamente' + (imagenHierro ? ' con imagen' : ''));
      }
      
      // Seleccionar el hierro creado
      setHierroSeleccionado(hierroCreado._id);
      
      // Limpiar formulario
      setNuevoHierro({
        nombre: '',
        finca_ids: [],
        dueno: '',
        numero_documento: '',
        fecha_ingreso: new Date().toISOString().split('T')[0],
        descripcion: ''
      });
      setImagenHierro(null);
      setPreviewImagen(null);
      
      // Cerrar modal
      setMostrarModalHierro(false);
      
      // Recargar la lista de hierros desde el backend para obtener datos actualizados (incluyendo imágenes)
      await loadHierros(fincaFiltro || undefined);
      
      // Recargar datos si estamos en resumen o movimientos
      if (activeTab === 'resumen') {
        loadResumen();
      } else if (activeTab === 'movimientos') {
        loadMovimientos();
      }
    } catch (err: any) {
      console.error('Error al crear hierro:', err);
      setError(err.response?.data?.error || 'Error al crear hierro');
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarHierro = async (): Promise<void> => {
    if (!hierroSeleccionado) {
      setError('Debe seleccionar un hierro para eliminar');
      return;
    }

    // Buscar el hierro seleccionado para mostrar su nombre
    const hierro = hierros.find(h => h._id === hierroSeleccionado);
    const nombreHierro = hierro?.nombre || 'este hierro';

    // Primera confirmación
    const confirmar = window.confirm(
      `¿Está seguro de que desea eliminar el hierro "${nombreHierro}"?\n\n` +
      `Esta acción marcará el hierro como eliminado (soft delete). ` +
      `Si necesita recuperar información eliminada, comuníquese con Soporte Ganando.`
    );

    if (!confirmar) {
      return;
    }

    // Segunda confirmación: solicitar contraseña
    const password = window.prompt(
      `Para confirmar la eliminación del hierro "${nombreHierro}", por favor ingrese su contraseña:`
    );

    // Si el usuario cancela o no ingresa contraseña, cancelar el proceso
    if (!password || password.trim() === '') {
      setError('Operación cancelada. Se requiere la contraseña para eliminar el hierro.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await apiService.delete<{
        message: string;
        hierro_eliminado: {
          _id: string;
          nombre: string;
          deleted_at: string;
          deleted_by: string;
        };
      }>(`/api/hierros/${hierroSeleccionado}`, {
        data: {
          password: password.trim()
        }
      });

      setSuccess(`Hierro "${nombreHierro}" eliminado correctamente`);
      
      // Limpiar selección
      setHierroSeleccionado('');
      
      // Recargar la lista de hierros
      await loadHierros(fincaFiltro || undefined);
      
      // Recargar datos si estamos en resumen o movimientos
      if (activeTab === 'resumen') {
        loadResumen();
      } else if (activeTab === 'movimientos') {
        loadMovimientos();
      }
    } catch (err: any) {
      console.error('Error al eliminar hierro:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Error al eliminar hierro');
    } finally {
      setLoading(false);
    }
  };

  const loadCategorias = async (): Promise<void> => {
    try {
      const response = await apiService.get<CategoriasResponse>('/api/inventario/categorias');
      setCategorias(response.data.categorias);
    } catch (err: any) {
      console.error('Error al cargar categorías:', err);
      setError('Error al cargar categorías');
    }
  };

  const loadTiposMovimiento = async (): Promise<void> => {
    try {
      const response = await apiService.get<TiposMovimientoResponse>('/api/inventario/tipos-movimiento');
      let tipos: TipoMovimiento[] = response.data.tipos_movimiento.map((t) => ({
        ...t,
        descripcion: descripcionTipoMovimiento(t.tipo, t.descripcion),
      }));

      // Botón único de reclasificación (API expone reclasificacion_resta / reclasificacion_suma)
      const existeReclasificacionLegacy = tipos.some((t) => t.tipo === 'reclasificacion');
      if (!existeReclasificacionLegacy) {
        tipos = [
          ...tipos,
          {
            tipo: TIPO_RECLASIFICACION_UI,
            efecto: 'reclasificacion' as const,
            descripcion: descripcionTipoMovimiento(TIPO_RECLASIFICACION_UI),
          },
        ];
      }

      setTiposMovimiento(tipos);
    } catch (err: any) {
      console.error('Error al cargar tipos de movimiento:', err);
      setError('Error al cargar tipos de movimiento');
    }
  };

  const loadResumen = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (hierroSeleccionado) {
        // Buscar el nombre del hierro seleccionado
        const hierro = hierros.find(h => h._id === hierroSeleccionado);
        if (hierro) {
          params.append('hierro', hierro.nombre);
        }
      }
      if (fincaFiltro) {
        params.append('finca_id', fincaFiltro);
      }
      if (!INVENTARIO_FINCA_PERMITE_VALOR) {
        params.append('excluir_valor', '1');
      }
      if (!INVENTARIO_FINCA_PERMITE_PESO) {
        params.append('excluir_peso', '1');
      }
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await apiService.get<ResumenResponse>(`/api/inventario/resumen${queryString}`);
      setResumen(sanitizarResumenInventarioFinca(response.data.resumen));
    } catch (err: any) {
      console.error('Error al cargar resumen:', err);
      setError(err.response?.data?.error || 'Error al cargar resumen de inventario');
    } finally {
      setLoading(false);
    }
  };

  const loadMovimientos = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (hierroSeleccionado) {
        // Buscar el nombre del hierro seleccionado para usar el filtro 'hierro'
        const hierro = hierros.find(h => h._id === hierroSeleccionado);
        if (hierro) {
          params.append('hierro', hierro.nombre);
        }
        // También enviar hierro_id si el backend lo requiere
        params.append('hierro_id', hierroSeleccionado);
      }
      if (fincaFiltro) {
        params.append('finca_id', fincaFiltro);
      }
      if (filtrosMovimientos.categoria) params.append('categoria', filtrosMovimientos.categoria);
      if (filtrosMovimientos.tipo_movimiento) params.append('tipo_movimiento', filtrosMovimientos.tipo_movimiento);
      if (filtrosMovimientos.libro_id) params.append('libro_id', filtrosMovimientos.libro_id);
      if (filtrosMovimientos.fecha_inicio) params.append('fecha_inicio', filtrosMovimientos.fecha_inicio);
      if (filtrosMovimientos.fecha_fin) params.append('fecha_fin', filtrosMovimientos.fecha_fin);
      params.append('page', pagination.page.toString());
      params.append('per_page', pagination.per_page.toString());
      if (!INVENTARIO_FINCA_PERMITE_VALOR) {
        params.append('excluir_valor', '1');
      }
      if (!INVENTARIO_FINCA_PERMITE_PESO) {
        params.append('excluir_peso', '1');
      }

      const response = await apiService.get<MovimientosResponse>(`/api/inventario/movimientos?${params.toString()}`);
      const movimientosSanitizados = sanitizarMovimientosInventarioFinca(response.data.movimientos);
      setMovimientos(ordenarMovimientosPorCategoriaCreacion(movimientosSanitizados));
      setPagination({
        page: response.data.page,
        per_page: response.data.per_page,
        total: response.data.total,
        total_pages: response.data.total_pages
      });
    } catch (err: any) {
      console.error('Error al cargar movimientos:', err);
      setError(err.response?.data?.error || 'Error al cargar movimientos');
    } finally {
      setLoading(false);
    }
  };

  const loadLibrosInventario = async (fincaId: string): Promise<void> => {
    try {
      setLoadingLibros(true);
      const response = await apiService.get<LibrosInventarioResponse>(`/api/inventario/libros?finca_id=${encodeURIComponent(fincaId)}`);
      setLibrosInventario(response.data.libros || []);
    } catch (err: any) {
      console.error('Error al cargar libros de inventario:', err);
      setLibrosInventario([]);
      setError(err.response?.data?.error || 'Error al cargar libros de inventario');
    } finally {
      setLoadingLibros(false);
    }
  };

  const handleCrearLibro = async (): Promise<void> => {
    if (!fincaFiltro) {
      setError('Debe seleccionar una finca para crear un libro');
      return;
    }
    const nombre = nuevoLibroNombre.trim();
    if (!nombre) {
      setError('Ingrese el nombre del nuevo libro');
      return;
    }

    try {
      setGuardandoLibro(true);
      setError(null);
      const response = await apiService.post('/api/inventario/libros', {
        nombre,
        finca_id: fincaFiltro
      });
      const libroCreado = response.data?.libro as LibroInventario | undefined;
      await loadLibrosInventario(fincaFiltro);
      if (libroCreado?.libro_id) {
        setNuevoMovimiento(prev => ({ ...prev, libro_id: libroCreado.libro_id }));
      }
      setNuevoLibroNombre('');
      setMostrarNuevoLibro(false);
      setSuccess('Libro creado correctamente');
    } catch (err: any) {
      console.error('Error al crear libro:', err);
      setError(err.response?.data?.error || 'Error al crear libro');
    } finally {
      setGuardandoLibro(false);
    }
  };

  const handleCrearMovimiento = async (e: React.SyntheticEvent): Promise<void> => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Validar que haya categorías seleccionadas
      if (categoriasSeleccionadas.length === 0) {
        setError('Debe seleccionar al menos una categoría con tipo de movimiento y cantidad');
        setLoading(false);
        return;
      }

      // Validar campos requeridos
      if (!nuevoMovimiento.libro_id || !nuevoMovimiento.hoja || !nuevoMovimiento.hierro || !fincaFiltro) {
        setError('Debe completar todos los campos requeridos (libro, hoja, hierro y finca)');
        setLoading(false);
        return;
      }

      // Crear un movimiento por cada categoría seleccionada
      const promesasMovimientos = categoriasSeleccionadas.map(async (catSel) => {
      const movimientoData: Record<string, unknown> = {
        libro_id: nuevoMovimiento.libro_id,
        hoja: parseInt(nuevoMovimiento.hoja),
        fecha: nuevoMovimiento.fecha,
        hierro_id: nuevoMovimiento.hierro,
        finca_id: fincaFiltro,
        categoria: catSel.codigo,
        tipo_movimiento: catSel.tipo_movimiento,
        cantidad: catSel.cantidad,
        observaciones: catSel.observaciones?.trim() || undefined
      };
        if (INVENTARIO_FINCA_PERMITE_PESO && catSel.peso !== undefined) {
          movimientoData.peso = catSel.peso;
        }
        if (INVENTARIO_FINCA_PERMITE_VALOR && catSel.valor !== undefined) {
          movimientoData.valor = catSel.valor;
        }
        return apiService.post('/api/inventario/movimientos', movimientoData);
      });

      await Promise.all(promesasMovimientos);
      
      setSuccess(`Movimiento(s) creado(s) correctamente para ${categoriasSeleccionadas.length} categoría(s)`);
      
      // Limpiar formulario y selecciones
      setNuevoMovimiento({
        libro_id: '',
        hoja: '',
        fecha: new Date().toISOString().split('T')[0],
        hierro: hierroSeleccionado || '',
        categoria: '',
        tipo_movimiento: '',
        cantidad: ''
      });
      setCategoriasSeleccionadas([]);

      // Recargar datos
      loadResumen();
      loadMovimientos();
      
      // Cambiar a tab de movimientos para ver los nuevos registros
      setTimeout(() => {
        setActiveTab('movimientos');
      }, 1500);
    } catch (err: any) {
      console.error('Error al crear movimiento:', err);
      setError(err.response?.data?.error || 'Error al crear movimiento');
    } finally {
      setLoading(false);
    }
  };

  const handleLimpiarFiltros = (): void => {
    setFiltrosMovimientos({
      categoria: '',
      tipo_movimiento: '',
      libro_id: '',
      fecha_inicio: '',
      fecha_fin: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getEfectoColor = (efecto: string): string => {
    if (efecto === 'suma') return '#4caf50';
    if (efecto === 'resta') return '#f44336';
    if (efecto === 'reclasificacion') return '#2196f3';
    return '#666';
  };

  const getEfectoIcono = (efecto: string): string => {
    if (efecto === 'suma') return '➕';
    if (efecto === 'resta') return '➖';
    if (efecto === 'reclasificacion') return '🔄';
    return '•';
  };

  // Función para formatear número como pesos colombianos
  const formatearPesos = (valor: string): string => {
    // Remover todo excepto números y punto (para permitir decimales)
    const soloNumeros = valor.replace(/[^\d.]/g, '');
    if (!soloNumeros) return '';
    
    // Separar parte entera y decimal
    const partes = soloNumeros.split('.');
    let parteEntera = partes[0] || '';
    const parteDecimal = partes[1] || '';
    
    // Formatear parte entera con comilla simple (') como separador de miles
    const formatearParteEntera = (numStr: string): string => {
      if (!numStr) return '';
      const partes = [];
      let contador = 0;
      
      // Recorrer desde el final y agregar comilla cada 3 dígitos
      for (let i = numStr.length - 1; i >= 0; i--) {
        if (contador === 3 && i >= 0) {
          partes.unshift("'");
          contador = 0;
        }
        partes.unshift(numStr[i]);
        contador++;
      }
      
      return partes.join('');
    };
    
    const enteraFormateada = formatearParteEntera(parteEntera);
    const decimalFormateado = parteDecimal ? `,${parteDecimal}` : '';
    
    return `$${enteraFormateada}${decimalFormateado}`;
  };

  // Función para extraer solo números (y punto para decimales) de un valor formateado
  const extraerNumeros = (valor: string): string => {
    // Permitir números, punto y coma (convertir coma a punto para decimales)
    let numeros = valor.replace(/[^\d.,]/g, '');
    // Convertir coma a punto para decimales
    numeros = numeros.replace(/,/g, '.');
    // Asegurar que solo haya un punto
    const partes = numeros.split('.');
    if (partes.length > 2) {
      return partes[0] + '.' + partes.slice(1).join('');
    }
    return numeros;
  };

  // Función para formatear número con decimales más pequeños (50% del tamaño)
  const formatearNumeroConDecimales = (numero: number, decimales: number = 2): React.JSX.Element => {
    const formateado = numero.toLocaleString('es-CO', { 
      minimumFractionDigits: decimales, 
      maximumFractionDigits: decimales 
    });
    const partes = formateado.split(',');
    if (partes.length === 2) {
      return (
        <>
          {partes[0]},<span className="decimal-part">{partes[1]}</span>
        </>
      );
    }
    return <>{formateado}</>;
  };

  // Función para abrir modal de tipo de movimiento al hacer clic en categoría
  const handleCategoriaClick = (categoria: Categoria): void => {
    setCategoriaSeleccionadaModal(categoria);
    setMostrarModalTipoMovimiento(true);
  };

  // Función para seleccionar tipo de movimiento y abrir modal de cantidad
  const handleTipoMovimientoClick = (tipo: TipoMovimiento): void => {
    setTipoMovimientoSeleccionadoModal(tipo);
    setMostrarModalTipoMovimiento(false);
    setMostrarModalCantidad(true);
  };

  // Función para confirmar cantidad y abrir modal de peso (o continuar sin peso/valor según política)
  const handleConfirmarCantidad = (): void => {
    if (!categoriaSeleccionadaModal || !tipoMovimientoSeleccionadoModal || !cantidadTemporal || parseInt(cantidadTemporal) <= 0) {
      setError('Por favor ingrese una cantidad válida');
      return;
    }

    const cantidadNum = parseFloat(cantidadTemporal);
    setCantidadTemporal(cantidadNum.toString());
    setMostrarModalCantidad(false);
    setError(null);

    if (INVENTARIO_FINCA_PERMITE_PESO) {
      setMostrarModalPeso(true);
      return;
    }

    if (tipoMovimientoSeleccionadoModal.efecto === 'reclasificacion') {
      setMostrarModalCategoriaDestino(true);
      return;
    }

    const nuevaSeleccion: CategoriaSeleccionada = {
      codigo: categoriaSeleccionadaModal.codigo,
      nombre: categoriaSeleccionadaModal.nombre,
      tipo_movimiento: tipoMovimientoSeleccionadoModal.tipo,
      tipo_movimiento_descripcion: tipoMovimientoSeleccionadoModal.descripcion,
      cantidad: cantidadNum,
      observaciones: '',
      mostrarObservacion: false
    };

    setCategoriasSeleccionadas(prev => {
      const filtradas = prev.filter(c => c.codigo !== categoriaSeleccionadaModal.codigo);
      return [...filtradas, nuevaSeleccion];
    });

    setCategoriaSeleccionadaModal(null);
    setTipoMovimientoSeleccionadoModal(null);
    setCantidadTemporal('');
    setPesoTemporal('');
    setValorTemporal('');
  };

  // Función para confirmar peso y abrir valor o finalizar según política
  const handleConfirmarPeso = (): void => {
    if (!categoriaSeleccionadaModal || !tipoMovimientoSeleccionadoModal || !cantidadTemporal || !pesoTemporal) {
      setError('Por favor ingrese una cantidad y peso válidos');
      return;
    }

    const cantidadNum = parseFloat(cantidadTemporal);
    const pesoNum = parseFloat(pesoTemporal);

    if (cantidadNum <= 0 || pesoNum <= 0) {
      setError('Por favor ingrese valores válidos para cantidad y peso');
      return;
    }

    setCantidadTemporal(cantidadNum.toString());
    setPesoTemporal(pesoNum.toString());
    setMostrarModalPeso(false);
    setError(null);

    if (INVENTARIO_FINCA_PERMITE_VALOR) {
      setValorTemporal('');
      setMostrarModalValor(true);
      return;
    }

    if (tipoMovimientoSeleccionadoModal.efecto === 'reclasificacion') {
      setMostrarModalCategoriaDestino(true);
      return;
    }

    const nuevaSeleccion: CategoriaSeleccionada = {
      codigo: categoriaSeleccionadaModal.codigo,
      nombre: categoriaSeleccionadaModal.nombre,
      tipo_movimiento: tipoMovimientoSeleccionadoModal.tipo,
      tipo_movimiento_descripcion: tipoMovimientoSeleccionadoModal.descripcion,
      cantidad: cantidadNum,
      peso: pesoNum,
      observaciones: '',
      mostrarObservacion: false
    };

    setCategoriasSeleccionadas(prev => {
      const filtradas = prev.filter(c => c.codigo !== categoriaSeleccionadaModal.codigo);
      return [...filtradas, nuevaSeleccion];
    });

    setCategoriaSeleccionadaModal(null);
    setTipoMovimientoSeleccionadoModal(null);
    setCantidadTemporal('');
    setPesoTemporal('');
    setValorTemporal('');
  };

  const handleConfirmarValor = (): void => {
    if (!INVENTARIO_FINCA_PERMITE_VALOR) {
      return;
    }
    if (!categoriaSeleccionadaModal || !tipoMovimientoSeleccionadoModal || !cantidadTemporal || !pesoTemporal || valorTemporal === null || valorTemporal === undefined || valorTemporal === '') {
      setError('Por favor ingrese todos los valores requeridos');
      return;
    }

    const cantidadNum = parseFloat(cantidadTemporal);
    const pesoNum = parseFloat(pesoTemporal);
    const valorNum = parseFloat(valorTemporal);

    if (cantidadNum <= 0 || pesoNum <= 0 || valorNum < 0 || isNaN(valorNum)) {
      setError('Por favor ingrese valores válidos para cantidad, peso y valor');
      return;
    }

    // Si es reclasificación, abrir modal de categoría destino
    if (tipoMovimientoSeleccionadoModal.efecto === 'reclasificacion') {
      setMostrarModalValor(false);
      setMostrarModalCategoriaDestino(true);
      setError(null);
      return;
    }

    // Para movimientos normales, guardar directamente
    const nuevaSeleccion: CategoriaSeleccionada = {
      codigo: categoriaSeleccionadaModal.codigo,
      nombre: categoriaSeleccionadaModal.nombre,
      tipo_movimiento: tipoMovimientoSeleccionadoModal.tipo,
      tipo_movimiento_descripcion: tipoMovimientoSeleccionadoModal.descripcion,
      cantidad: cantidadNum,
      peso: pesoNum,
      ...(INVENTARIO_FINCA_PERMITE_VALOR ? { valor: valorNum } : {}),
      observaciones: '',
      mostrarObservacion: false
    };

    setCategoriasSeleccionadas(prev => {
      const filtradas = prev.filter(c => c.codigo !== categoriaSeleccionadaModal.codigo);
      return [...filtradas, nuevaSeleccion];
    });

    setMostrarModalValor(false);
    setCategoriaSeleccionadaModal(null);
    setTipoMovimientoSeleccionadoModal(null);
    setCantidadTemporal('');
    setPesoTemporal('');
    setValorTemporal('');
    setError(null);
  };

  // Función para confirmar reclasificación y guardar los dos movimientos
  const handleConfirmarReclasificacion = (): void => {
    if (!categoriaSeleccionadaModal || !categoriaDestinoReclasificacion || !tipoMovimientoSeleccionadoModal || !cantidadTemporal) {
      setError('Por favor seleccione una categoría destino');
      return;
    }

    if (categoriaSeleccionadaModal.codigo === categoriaDestinoReclasificacion.codigo) {
      setError('La categoría destino debe ser diferente a la categoría actual');
      return;
    }

    const cantidadNum = parseFloat(cantidadTemporal);
    if (cantidadNum <= 0 || isNaN(cantidadNum)) {
      setError('Por favor ingrese una cantidad válida');
      return;
    }

    let pesoNum: number | undefined;
    if (INVENTARIO_FINCA_PERMITE_PESO) {
      if (!pesoTemporal) {
        setError('Por favor ingrese el peso');
        return;
      }
      pesoNum = parseFloat(pesoTemporal);
      if (pesoNum <= 0 || isNaN(pesoNum)) {
        setError('Por favor ingrese un peso válido');
        return;
      }
    }

    let valorNum: number | undefined;
    if (INVENTARIO_FINCA_PERMITE_VALOR) {
      if (valorTemporal === null || valorTemporal === undefined || valorTemporal === '') {
        setError('Por favor ingrese el valor');
        return;
      }
      valorNum = parseFloat(valorTemporal);
      if (valorNum < 0 || isNaN(valorNum)) {
        setError('Por favor ingrese un valor válido');
        return;
      }
    }

    const tipoSalida =
      tiposMovimiento.find((t) => t.tipo === TIPO_RECLASIFICACION_RESTA) ??
      tiposMovimiento.find((t) => t.descripcion.includes('Reclasificación interna - Salida'));
    const tipoIngreso =
      tiposMovimiento.find((t) => t.tipo === TIPO_RECLASIFICACION_SUMA) ??
      tiposMovimiento.find((t) => t.descripcion.includes('Reclasificación interna - Ingreso'));

    if (!tipoSalida || !tipoIngreso) {
      setError('Error: No se encontraron los tipos de movimiento de reclasificación (reclasificacion_resta / reclasificacion_suma)');
      return;
    }

    // Crear dos selecciones: una para salida y otra para ingreso
    const seleccionSalida: CategoriaSeleccionada = {
      codigo: categoriaSeleccionadaModal.codigo,
      nombre: categoriaSeleccionadaModal.nombre,
      tipo_movimiento: tipoSalida.tipo,
      tipo_movimiento_descripcion: tipoSalida.descripcion,
      cantidad: cantidadNum,
      ...(pesoNum !== undefined ? { peso: pesoNum } : {}),
      ...(valorNum !== undefined ? { valor: valorNum } : {}),
      observaciones: '',
      mostrarObservacion: false,
      categoria_destino: categoriaDestinoReclasificacion.codigo // Agregar categoría destino
    };

    const seleccionIngreso: CategoriaSeleccionada = {
      codigo: categoriaDestinoReclasificacion.codigo,
      nombre: categoriaDestinoReclasificacion.nombre,
      tipo_movimiento: tipoIngreso.tipo,
      tipo_movimiento_descripcion: tipoIngreso.descripcion,
      cantidad: cantidadNum,
      ...(pesoNum !== undefined ? { peso: pesoNum } : {}),
      ...(valorNum !== undefined ? { valor: valorNum } : {}),
      observaciones: '',
      mostrarObservacion: false,
      categoria_origen: categoriaSeleccionadaModal.codigo // Agregar categoría origen
    };

    // Agregar ambas selecciones
    setCategoriasSeleccionadas(prev => {
      // Eliminar cualquier selección previa de estas categorías
      const filtradas = prev.filter(c => 
        c.codigo !== categoriaSeleccionadaModal.codigo && 
        c.codigo !== categoriaDestinoReclasificacion.codigo
      );
      return [...filtradas, seleccionSalida, seleccionIngreso];
    });

    // Limpiar estados
    setMostrarModalCategoriaDestino(false);
    setCategoriaSeleccionadaModal(null);
    setCategoriaDestinoReclasificacion(null);
    setTipoMovimientoSeleccionadoModal(null);
    setCantidadTemporal('');
    setPesoTemporal('');
    setValorTemporal('');
    setError(null);
  };

  // Función para eliminar selección de categoría
  const handleEliminarCategoriaSeleccionada = (codigo: string): void => {
    setCategoriasSeleccionadas(prev => {
      // Buscar si esta categoría es parte de una reclasificación
      const categoria = prev.find(c => c.codigo === codigo);
      
      // Si tiene categoria_destino o categoria_origen, es parte de una reclasificación
      if (categoria && (categoria.categoria_destino || categoria.categoria_origen)) {
        // Eliminar ambas categorías de la reclasificación
        const codigoDestino = categoria.categoria_destino;
        const codigoOrigen = categoria.categoria_origen;
        
        return prev.filter(c => {
          // Eliminar la categoría actual
          if (c.codigo === codigo) return false;
          // Si tiene relación con esta reclasificación, también eliminarla
          if (codigoDestino && (c.codigo === codigoDestino || c.categoria_origen === codigo)) return false;
          if (codigoOrigen && (c.codigo === codigoOrigen || c.categoria_destino === codigo)) return false;
          return true;
        });
      }
      
      // Para movimientos normales, solo eliminar la categoría seleccionada
      return prev.filter(c => c.codigo !== codigo);
    });
  };

  const handleToggleObservacionCategoria = (codigo: string): void => {
    setCategoriasSeleccionadas(prev =>
      prev.map((cat) =>
        cat.codigo === codigo
          ? { ...cat, mostrarObservacion: !cat.mostrarObservacion }
          : cat
      )
    );
  };

  const handleObservacionCategoriaChange = (codigo: string, observaciones: string): void => {
    setCategoriasSeleccionadas(prev =>
      prev.map((cat) =>
        cat.codigo === codigo ? { ...cat, observaciones } : cat
      )
    );
  };

  return (
    <div className="inventario-ganado-container">
      <Helmet>
        <title>Inventario por Finca - Ganando</title>
        <meta property="og:title" content="Inventario por Finca - Ganando" />
      </Helmet>

      {loading && <LoadingOverlay isLoading={loading} />}

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`tab-button ${activeTab === 'resumen' ? 'active' : ''}`}
          onClick={() => setActiveTab('resumen')}
        >
          Resumen de Inventario
        </button>
        <button
          className={`tab-button ${activeTab === 'movimientos' ? 'active' : ''}`}
          onClick={() => setActiveTab('movimientos')}
        >
          Movimientos
        </button>
        <button
          className={`tab-button ${activeTab === 'nuevo' ? 'active' : ''}`}
          onClick={() => setActiveTab('nuevo')}
        >
          Nuevo Movimiento
        </button>
      </div>

      {/* Mensajes de error y éxito */}
      {error && (
        <div className="alert-error" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}
      {success && (
        <div className="alert-success" style={{ marginBottom: '20px' }}>
          {success}
        </div>
      )}

      {/* Tab Content */}
      <div className="tab-content">
        {/* Tab: Resumen */}
        {activeTab === 'resumen' && (
          <div className="tab-content-inner">
            <div className="tab-header">
              <div className="tab-controls">
                <div className="filters-row" style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'nowrap', width: '100%' }}>
                  <h2 style={{ margin: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>Resumen de Inventario</h2>
                  <CustomDropdown
                    className="custom-dropdown-toggle"
                    value={fincaFiltro}
                    placeholder={placeholderFincaFiltro}
                    options={opcionesFiltroFinca}
                    isOpen={dropdownFincaActivo === 'resumen'}
                    onToggle={() => setDropdownFincaActivo((prev) => prev === 'resumen' ? null : 'resumen')}
                    onSelect={(value) => {
                      setFincaFiltro(value);
                      setHierroSeleccionado('');
                      setDropdownFincaActivo(null);
                    }}
                    containerRef={dropdownFincaRef}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'nowrap', flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={() => setHierroSeleccionado('')}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: hierroSeleccionado === '' ? '2px solid #4caf50' : '1px solid #ddd',
                        backgroundColor: hierroSeleccionado === '' ? '#f0f8f0' : 'white',
                        cursor: 'pointer',
                        fontWeight: hierroSeleccionado === '' ? 'bold' : 'normal',
                        transition: 'all 0.2s',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}
                      title="Ver todos los hierros"
                    >
                      Todos
                    </button>
                    <div
                      className="hierros-container-primera-linea"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 70px)',
                        gap: '10px',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        flex: '1 1 auto',
                        minWidth: 0
                      }}
                    >
                      {hierros.length === 0 ? (
                        <span style={{ color: '#999', fontSize: '14px', whiteSpace: 'nowrap' }}>No hay hierros disponibles</span>
                      ) : (
                        hierros.map((hierro) => (
                          <button
                            key={hierro._id}
                            type="button"
                            onClick={() => setHierroSeleccionado(hierro._id)}
                            style={{
                              width: '70px',
                              height: '70px',
                              padding: '4px',
                              border: hierroSeleccionado === hierro._id ? '3px solid #4caf50' : '2px solid #ddd',
                              borderRadius: '6px',
                              backgroundColor: hierroSeleccionado === hierro._id ? '#f0f8f0' : 'white',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              transition: 'all 0.2s',
                              boxShadow: hierroSeleccionado === hierro._id ? '0 2px 4px rgba(76, 175, 80, 0.3)' : '0 1px 2px rgba(0,0,0,0.1)',
                              flexShrink: 0
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.border = '3px solid #4caf50';
                              e.currentTarget.style.boxShadow = '0 2px 6px rgba(76, 175, 80, 0.4)';
                              e.currentTarget.style.transform = 'scale(1.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.border = hierroSeleccionado === hierro._id ? '3px solid #4caf50' : '2px solid #ddd';
                              e.currentTarget.style.boxShadow = hierroSeleccionado === hierro._id ? '0 2px 4px rgba(76, 175, 80, 0.3)' : '0 1px 2px rgba(0,0,0,0.1)';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                            title={hierro.nombre}
                          >
                            {getImagenUrl(hierro.imagen_url, hierro.imagen) ? (
                              <img
                                src={getImagenUrl(hierro.imagen_url, hierro.imagen) || ''}
                                alt={hierro.nombre}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                  pointerEvents: 'none'
                                }}
                                onError={(e) => {
                                  // Si la imagen falla al cargar, ocultarla
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : (
                              <span style={{ fontSize: '24px', pointerEvents: 'none' }}>🏷️</span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                {/* Botones de hierros - debajo de filters-row */}
                {/* Segunda línea: botones - solo cuando hay más de 7 hierros */}
                {(hierros.length > 7) && !isAdminFinca() && (
                  <div className="hierros-tercera-linea-botones" style={{ 
                    display: 'flex', 
                    flexDirection: 'row',
                    gap: '8px', 
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    marginTop: '10px'
                  }}>
                    <button
                      className="button"
                      onClick={() => setMostrarModalHierro(true)}
                      title="Agregar nuevo hierro"
                      style={{ 
                        padding: '8px 12px', 
                        background: '#4caf50', 
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        minWidth: '140px'
                      }}
                    >
                      + Nuevo Hierro
                    </button>
                    <button
                      className="button"
                      onClick={handleEliminarHierro}
                      disabled={!hierroSeleccionado}
                      title={hierroSeleccionado ? "Eliminar hierro seleccionado" : "Seleccione un hierro para eliminar"}
                      style={{ 
                        padding: '8px 12px', 
                        background: hierroSeleccionado ? 'rgba(255, 179, 0, 0.4)' : '#ccc',
                        color: hierroSeleccionado ? 'white' : '#666',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: hierroSeleccionado ? 'pointer' : 'not-allowed',
                        minWidth: '140px',
                        transition: 'all 0.2s',
                        opacity: hierroSeleccionado ? 1 : 0.6
                      }}
                      onMouseEnter={(e) => {
                        if (hierroSeleccionado) {
                          e.currentTarget.style.background = 'rgba(255, 179, 0, 0.5)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(255, 179, 0, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (hierroSeleccionado) {
                          e.currentTarget.style.background = 'rgba(255, 179, 0, 0.4)';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                    >
                      🗑️ Eliminar Hierro
                    </button>
                    {fincaFiltro && !loading && totalesInventarioFincaResumen && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          gap: '5px',
                          marginLeft: 'auto'
                        }}
                      >
                        <span className="resumen-total">
                          Total inventario finca: {totalesInventarioFincaResumen.cantidad}
                        </span>
                        {INVENTARIO_FINCA_PERMITE_VALOR && totalesInventarioFincaResumen.valor > 0 && (
                          <span className="resumen-total" style={{ color: '#4caf50', fontWeight: '600' }}>
                            Valor Total: ${totalesInventarioFincaResumen.valor.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </span>
                        )}
                        {INVENTARIO_FINCA_PERMITE_PESO && totalesInventarioFincaResumen.peso > 0 && (
                          <span className="resumen-total" style={{ color: '#2196F3', fontWeight: '600' }}>
                            Peso Total: {formatearNumeroConDecimales(totalesInventarioFincaResumen.peso, 2)} kg
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {/* Botones cuando hay 7 o menos hierros */}
                {hierros.length <= 7 && !isAdminFinca() && (
                  <div className="hierros-tercera-linea-botones" style={{ 
                    display: 'flex', 
                    flexDirection: 'row', 
                    gap: '8px', 
                    alignItems: 'center',
                    marginTop: '10px'
                  }}>
                    <button
                      className="button"
                      onClick={() => setMostrarModalHierro(true)}
                      title="Agregar nuevo hierro"
                      style={{ 
                        padding: '8px 12px', 
                        background: '#4caf50', 
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        minWidth: '140px'
                      }}
                    >
                      + Nuevo Hierro
                    </button>
                    <button
                      className="button"
                      onClick={handleEliminarHierro}
                      disabled={!hierroSeleccionado}
                      title={hierroSeleccionado ? "Eliminar hierro seleccionado" : "Seleccione un hierro para eliminar"}
                      style={{ 
                        padding: '8px 12px', 
                        background: hierroSeleccionado ? 'rgba(255, 179, 0, 0.4)' : '#ccc',
                        color: hierroSeleccionado ? 'white' : '#666',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: hierroSeleccionado ? 'pointer' : 'not-allowed',
                        minWidth: '140px',
                        transition: 'all 0.2s',
                        opacity: hierroSeleccionado ? 1 : 0.6
                      }}
                      onMouseEnter={(e) => {
                        if (hierroSeleccionado) {
                          e.currentTarget.style.background = 'rgba(255, 179, 0, 0.5)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(255, 179, 0, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (hierroSeleccionado) {
                          e.currentTarget.style.background = 'rgba(255, 179, 0, 0.4)';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                    >
                      🗑️ Eliminar Hierro
                    </button>
                    {fincaFiltro && !loading && totalesInventarioFincaResumen && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          gap: '5px',
                          marginLeft: 'auto'
                        }}
                      >
                        <span className="resumen-total">
                          Total inventario finca: {totalesInventarioFincaResumen.cantidad}
                        </span>
                        {INVENTARIO_FINCA_PERMITE_VALOR && totalesInventarioFincaResumen.valor > 0 && (
                          <span className="resumen-total" style={{ color: '#4caf50', fontWeight: '600' }}>
                            Valor Total: ${totalesInventarioFincaResumen.valor.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </span>
                        )}
                        {INVENTARIO_FINCA_PERMITE_PESO && totalesInventarioFincaResumen.peso > 0 && (
                          <span className="resumen-total" style={{ color: '#2196F3', fontWeight: '600' }}>
                            Peso Total: {formatearNumeroConDecimales(totalesInventarioFincaResumen.peso, 2)} kg
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="table-container">
              {loading ? (
                <div className="loading-container">
                  <Loader />
                </div>
              ) : resumen.length === 0 ? (
                <div className="empty-state">No hay datos de inventario disponibles</div>
              ) : (
                resumen.map((hierro, index) => {
                  const hierroData = hierros.find(h => h._id === hierro.hierro_id);
                  return (
                    <div key={index} className="resumen-hierro-card">
                      <div className="resumen-hierro-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          {hierroData && getImagenUrl(hierroData.imagen_url, hierroData.imagen) ? (
                            <img
                              src={getImagenUrl(hierroData.imagen_url, hierroData.imagen) || ''}
                              alt={hierro.hierro}
                              style={{
                                width: '60px',
                                height: '60px',
                                objectFit: 'cover',
                                borderRadius: '6px',
                                border: '2px solid #ddd'
                              }}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '60px',
                              height: '60px',
                              borderRadius: '6px',
                              border: '2px solid #ddd',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '24px',
                              backgroundColor: '#f5f5f5'
                            }}>
                              🏷️
                            </div>
                          )}
                          <h3 style={{ margin: 0 }}>{hierro.hierro}</h3>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
                          <span className="resumen-total">Total: {hierro.total_general}</span>
                          {INVENTARIO_FINCA_PERMITE_VALOR && hierro.valor_total !== undefined && hierro.valor_total > 0 && (
                            <span className="resumen-total" style={{ color: '#4caf50', fontWeight: '600' }}>
                              Valor Total: ${hierro.valor_total.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </span>
                          )}
                          {INVENTARIO_FINCA_PERMITE_PESO && hierro.peso_total !== undefined && hierro.peso_total > 0 && (
                            <span className="resumen-total" style={{ color: '#2196F3', fontWeight: '600' }}>
                              Peso Total: {formatearNumeroConDecimales(hierro.peso_total, 2)} kg
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="resumen-categorias-grid">
                        {hierro.categorias.map((cat, catIndex) => (
                          <div key={catIndex} className="resumen-categoria-card">
                            <div className="resumen-categoria-header">
                              <span className="resumen-categoria-codigo">{cat.codigo}</span>
                              <span className="resumen-categoria-nombre">{cat.nombre}</span>
                            </div>
                            <div className="resumen-categoria-stats">
                              <div className="resumen-stat">
                                <span className="resumen-stat-label">Total:</span>
                                <span className="resumen-stat-value">{cat.total}</span>
                              </div>
                              {INVENTARIO_FINCA_PERMITE_VALOR && cat.valor_total !== undefined && cat.valor_total > 0 && (
                                <div className="resumen-stat">
                                  <span className="resumen-stat-label">Valor:</span>
                                  <span className="resumen-stat-value" style={{ color: '#4caf50', fontWeight: '600' }}>
                                    ${cat.valor_total.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                  </span>
                                </div>
                              )}
                              {INVENTARIO_FINCA_PERMITE_PESO && cat.peso_total !== undefined && cat.peso_total > 0 && (
                                <div className="resumen-stat">
                                  <span className="resumen-stat-label">Peso:</span>
                                  <span className="resumen-stat-value">
                                    {formatearNumeroConDecimales(cat.peso_total, 2)} kg
                                  </span>
                                </div>
                              )}
                              <div className="resumen-stat">
                                <span className="resumen-stat-label">Movimientos:</span>
                                <span className="resumen-stat-value">{cat.movimientos}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Tab: Movimientos */}
        {activeTab === 'movimientos' && (
          <div className="tab-content-inner">
            <div className="tab-header">
              <div className="tab-controls">
                <div className="filters-row" style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'nowrap', width: '100%' }}>
                  <h2 style={{ margin: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>Movimientos de Inventario</h2>
                  {fincaFiltro && (
                    <button
                      type="button"
                      className={`button ${edicionMovimientosHabilitada ? '' : 'btn-primary'}`}
                      onClick={edicionMovimientosHabilitada ? handleCancelarEdicionMovimientos : handleHabilitarEdicionMovimientos}
                      style={{ padding: '8px 16px', fontSize: '14px' }}
                    >
                      {edicionMovimientosHabilitada ? 'Cancelar edición' : 'Editar'}
                    </button>
                  )}
                  <CustomDropdown
                    className="custom-dropdown-toggle"
                    value={fincaFiltro}
                    placeholder={placeholderFincaFiltro}
                    options={opcionesFiltroFinca}
                    isOpen={dropdownFincaActivo === 'movimientos'}
                    onToggle={() => setDropdownFincaActivo((prev) => prev === 'movimientos' ? null : 'movimientos')}
                    onSelect={(value) => {
                      setFincaFiltro(value);
                      setHierroSeleccionado('');
                      setDropdownFincaActivo(null);
                    }}
                    containerRef={dropdownFincaRef}
                  />
                  {fincaFiltro && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'nowrap', flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => setHierroSeleccionado('')}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: hierroSeleccionado === '' ? '2px solid #4caf50' : '1px solid #ddd',
                          backgroundColor: hierroSeleccionado === '' ? '#f0f8f0' : 'white',
                          cursor: 'pointer',
                          fontWeight: hierroSeleccionado === '' ? 'bold' : 'normal',
                          transition: 'all 0.2s',
                          whiteSpace: 'nowrap',
                          flexShrink: 0
                        }}
                        title="Ver todos los hierros"
                      >
                        Todos
                      </button>
                      <div
                        className="hierros-container-primera-linea"
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(5, 70px)',
                          gap: '10px',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          flex: '1 1 auto',
                          minWidth: 0
                        }}
                      >
                        {hierros.length === 0 ? (
                          <span style={{ color: '#999', fontSize: '14px', whiteSpace: 'nowrap' }}>No hay hierros disponibles</span>
                        ) : (
                          hierros.map((hierro) => (
                            <button
                              key={hierro._id}
                              type="button"
                              onClick={() => setHierroSeleccionado(hierro._id)}
                              style={{
                                width: '70px',
                                height: '70px',
                                padding: '4px',
                                border: hierroSeleccionado === hierro._id ? '3px solid #4caf50' : '2px solid #ddd',
                                borderRadius: '6px',
                                backgroundColor: hierroSeleccionado === hierro._id ? '#f0f8f0' : 'white',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                transition: 'all 0.2s',
                                boxShadow: hierroSeleccionado === hierro._id ? '0 2px 4px rgba(76, 175, 80, 0.3)' : '0 1px 2px rgba(0,0,0,0.1)',
                                flexShrink: 0
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.border = '3px solid #4caf50';
                                e.currentTarget.style.boxShadow = '0 2px 6px rgba(76, 175, 80, 0.4)';
                                e.currentTarget.style.transform = 'scale(1.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.border = hierroSeleccionado === hierro._id ? '3px solid #4caf50' : '2px solid #ddd';
                                e.currentTarget.style.boxShadow = hierroSeleccionado === hierro._id ? '0 2px 4px rgba(76, 175, 80, 0.3)' : '0 1px 2px rgba(0,0,0,0.1)';
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                              title={hierro.nombre}
                            >
                              {getImagenUrl(hierro.imagen_url, hierro.imagen) ? (
                                <img
                                  src={getImagenUrl(hierro.imagen_url, hierro.imagen) || ''}
                                  alt={hierro.nombre}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                    pointerEvents: 'none'
                                  }}
                                  onError={(e) => {
                                    // Si la imagen falla al cargar, ocultarla
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <span style={{ fontSize: '24px', pointerEvents: 'none' }}>🏷️</span>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Filtros */}
            <div className="filters-section">
              <div className="filters-row filtros-movimientos-row">
                <CustomDropdown
                  className="custom-dropdown-toggle"
                  value={filtrosMovimientos.categoria}
                  placeholder="Todas las categorías"
                  options={[
                    { value: '', label: 'Todas las categorías' },
                    ...categorias.map((cat) => ({ value: cat.codigo, label: `${cat.codigo} - ${cat.nombre}` }))
                  ]}
                  isOpen={dropdownFiltrosActivo === 'categoria'}
                  onToggle={() => setDropdownFiltrosActivo((prev) => prev === 'categoria' ? null : 'categoria')}
                  onSelect={(value) => {
                    setFiltrosMovimientos({ ...filtrosMovimientos, categoria: value });
                    setDropdownFiltrosActivo(null);
                  }}
                  containerRef={dropdownFiltrosRef}
                />
                <CustomDropdown
                  className="custom-dropdown-toggle"
                  value={filtrosMovimientos.tipo_movimiento}
                  placeholder="Todos los tipos"
                  options={[
                    { value: '', label: 'Todos los tipos' },
                    ...tiposMovimiento
                      .filter((tipo) => !esTipoMovimientoSoloSelectorUi(tipo.tipo))
                      .map((tipo) => ({ value: tipo.tipo, label: tipo.descripcion })),
                  ]}
                  isOpen={dropdownFiltrosActivo === 'tipo'}
                  onToggle={() => setDropdownFiltrosActivo((prev) => prev === 'tipo' ? null : 'tipo')}
                  onSelect={(value) => {
                    setFiltrosMovimientos({ ...filtrosMovimientos, tipo_movimiento: value });
                    setDropdownFiltrosActivo(null);
                  }}
                  containerRef={dropdownFiltrosRef}
                />
                <CustomDropdown
                  className="custom-dropdown-toggle"
                  value={filtrosMovimientos.libro_id}
                  placeholder={!fincaFiltro ? 'Seleccione finca...' : loadingLibros ? 'Cargando libros...' : 'Todos los libros'}
                  options={[
                    { value: '', label: !fincaFiltro ? 'Seleccione finca...' : loadingLibros ? 'Cargando libros...' : 'Todos los libros' },
                    ...librosInventario.map((libro) => ({ value: libro.libro_id, label: libro.nombre }))
                  ]}
                  isOpen={dropdownFiltrosActivo === 'libro'}
                  onToggle={() => setDropdownFiltrosActivo((prev) => prev === 'libro' ? null : 'libro')}
                  onSelect={(value) => {
                    setFiltrosMovimientos({ ...filtrosMovimientos, libro_id: value });
                    setDropdownFiltrosActivo(null);
                  }}
                  containerRef={dropdownFiltrosRef}
                  disabled={!fincaFiltro || loadingLibros}
                />
                <div 
                  className={`date-input-wrapper ${!filtrosMovimientos.fecha_inicio ? 'empty' : ''}`}
                  data-placeholder="Fecha inicial"
                >
                  <input
                    type="date"
                    className="filter-input date-input"
                    value={filtrosMovimientos.fecha_inicio}
                    onChange={(e) => setFiltrosMovimientos({ ...filtrosMovimientos, fecha_inicio: e.target.value })}
                  />
                </div>
                <span className="date-separator">a</span>
                <div 
                  className={`date-input-wrapper ${!filtrosMovimientos.fecha_fin ? 'empty' : ''}`}
                  data-placeholder="Fecha final"
                >
                  <input
                    type="date"
                    className="filter-input date-input"
                    value={filtrosMovimientos.fecha_fin}
                    onChange={(e) => setFiltrosMovimientos({ ...filtrosMovimientos, fecha_fin: e.target.value })}
                  />
                </div>
                <button
                  className="button"
                  onClick={handleLimpiarFiltros}
                  style={{ padding: '8px 16px', fontSize: '14px' }}
                >
                  Limpiar
                </button>
              </div>
            </div>

            {/* Tabla de movimientos */}
            <div className="table-container">
              {loading ? (
                <div className="loading-container">
                  <Loader />
                </div>
              ) : movimientos.length === 0 ? (
                <div className="empty-state">No hay movimientos registrados</div>
              ) : (
                <>
                  <div className="table-header">
                    <div className="table-header-cell">Fecha</div>
                    <div className="table-header-cell">Hierro</div>
                    <div className="table-header-cell">Categoría</div>
                    <div className="table-header-cell">Tipo</div>
                    <div className="table-header-cell">Cantidad</div>
                    <div className="table-header-cell">Libro</div>
                    <div className="table-header-cell">Hoja</div>
                    <div className="table-header-cell">Usuario</div>
                    <div className="table-header-cell">Observaciones</div>
                  </div>
                  <div className="table-body">
                    {movimientos.map((mov, index) => {
                      const movimientoId = getMovimientoId(mov);
                      const movimientoKey = getMovimientoKey(mov, index);
                      const editable = movimientosEditables[movimientoKey];
                      const enModoEdicion = edicionMovimientosHabilitada && !!editable;
                      const filaConDropdownAbierto = !!dropdownEdicionActivo && dropdownEdicionActivo.startsWith(`${movimientoKey}-`);
                      const tipoMov = tiposMovimiento.find(t => t.tipo === mov.tipo_movimiento);
                      const categoria = categorias.find(c => c.codigo === mov.categoria);
                      const currentUser = getCurrentUser();
                      const usuarioActual = currentUser?.user?.name || mov.usuario;
                      return (
                        <div key={index} className={`table-row ${filaConDropdownAbierto ? 'table-row-dropdown-open' : ''}`}>
                          <div className="table-cell" data-label="Fecha">
                            {enModoEdicion ? (
                              <input
                                type="date"
                                className="table-cell-input"
                                value={editable?.fecha || ''}
                                onChange={(e) => handleEditableFieldChange(movimientoKey, 'fecha', e.target.value)}
                              />
                            ) : (
                              new Date(mov.fecha).toLocaleDateString('es-ES')
                            )}
                          </div>
                          <div className={`table-cell ${enModoEdicion ? 'table-cell-dropdown' : ''}`} data-label="Hierro">
                            {enModoEdicion ? (
                              <CustomDropdown
                                value={editable?.hierro || ''}
                                placeholder="Seleccione hierro..."
                                options={hierros.map((hierro) => ({ value: hierro.nombre, label: hierro.nombre }))}
                                isOpen={dropdownEdicionActivo === `${movimientoKey}-hierro`}
                                onToggle={() => setDropdownEdicionActivo((prev) => prev === `${movimientoKey}-hierro` ? null : `${movimientoKey}-hierro`)}
                                onSelect={(value) => {
                                  handleEditableFieldChange(movimientoKey, 'hierro', value);
                                  setDropdownEdicionActivo(null);
                                }}
                                containerRef={dropdownEdicionRef}
                              />
                            ) : (
                              mov.hierro
                            )}
                          </div>
                          <div className={`table-cell ${enModoEdicion ? 'table-cell-dropdown' : ''}`} data-label="Categoría">
                            {enModoEdicion ? (
                              <CustomDropdown
                                value={editable?.categoria || ''}
                                placeholder="Seleccione categoría..."
                                options={categorias.map((cat) => ({ value: cat.codigo, label: `${cat.codigo} - ${cat.nombre}` }))}
                                isOpen={dropdownEdicionActivo === `${movimientoKey}-categoria`}
                                onToggle={() => setDropdownEdicionActivo((prev) => prev === `${movimientoKey}-categoria` ? null : `${movimientoKey}-categoria`)}
                                onSelect={(value) => {
                                  handleEditableFieldChange(movimientoKey, 'categoria', value);
                                  setDropdownEdicionActivo(null);
                                }}
                                containerRef={dropdownEdicionRef}
                              />
                            ) : (
                              <>
                                <span className="categoria-badge">{mov.categoria}</span>
                                <span className="categoria-nombre">{categoria?.nombre || mov.categoria}</span>
                              </>
                            )}
                          </div>
                          <div className={`table-cell ${enModoEdicion ? 'table-cell-dropdown' : ''}`} data-label="Tipo">
                            {enModoEdicion ? (
                              <CustomDropdown
                                value={editable?.tipo_movimiento || ''}
                                placeholder="Seleccione tipo..."
                                options={tiposMovimiento
                                  .filter((tipo) => !esTipoMovimientoSoloSelectorUi(tipo.tipo))
                                  .map((tipo) => ({ value: tipo.tipo, label: tipo.descripcion }))}
                                isOpen={dropdownEdicionActivo === `${movimientoKey}-tipo_movimiento`}
                                onToggle={() => setDropdownEdicionActivo((prev) => prev === `${movimientoKey}-tipo_movimiento` ? null : `${movimientoKey}-tipo_movimiento`)}
                                onSelect={(value) => {
                                  handleEditableFieldChange(movimientoKey, 'tipo_movimiento', value);
                                  setDropdownEdicionActivo(null);
                                }}
                                containerRef={dropdownEdicionRef}
                              />
                            ) : (
                              <span
                                style={{
                                  color: tipoMov ? getEfectoColor(tipoMov.efecto) : '#333',
                                  fontWeight: '600'
                                }}
                              >
                                {tipoMov ? getEfectoIcono(tipoMov.efecto) : ''}{' '}
                                {tipoMov?.descripcion || descripcionTipoMovimiento(mov.tipo_movimiento)}
                              </span>
                            )}
                          </div>
                          <div className="table-cell" data-label="Cantidad" style={{ fontWeight: '600' }}>
                            {enModoEdicion ? (
                              <input
                                type="number"
                                min="1"
                                className="table-cell-input"
                                value={editable?.cantidad || ''}
                                onChange={(e) => handleEditableFieldChange(movimientoKey, 'cantidad', e.target.value)}
                              />
                            ) : (
                              mov.cantidad
                            )}
                          </div>
                          <div className={`table-cell ${enModoEdicion ? 'table-cell-dropdown' : ''}`} data-label="Libro">
                            {enModoEdicion ? (
                              <CustomDropdown
                                value={editable?.libro_id || ''}
                                placeholder="Seleccione libro..."
                                options={librosInventario.map((libro) => ({ value: libro.libro_id, label: libro.nombre }))}
                                isOpen={dropdownEdicionActivo === `${movimientoKey}-libro_id`}
                                onToggle={() => setDropdownEdicionActivo((prev) => prev === `${movimientoKey}-libro_id` ? null : `${movimientoKey}-libro_id`)}
                                onSelect={(value) => {
                                  handleEditableFieldChange(movimientoKey, 'libro_id', value);
                                  setDropdownEdicionActivo(null);
                                }}
                                containerRef={dropdownEdicionRef}
                              />
                            ) : (
                              mov.libro_nombre || mov.libro || '-'
                            )}
                          </div>
                          <div className="table-cell" data-label="Hoja">
                            {enModoEdicion ? (
                              <input
                                type="number"
                                min="1"
                                className="table-cell-input"
                                value={editable?.hoja || ''}
                                onChange={(e) => handleEditableFieldChange(movimientoKey, 'hoja', e.target.value)}
                              />
                            ) : (
                              mov.hoja
                            )}
                          </div>
                          <div className="table-cell" data-label="Usuario">{enModoEdicion ? usuarioActual : mov.usuario}</div>
                          <div className="table-cell" data-label="Observaciones">
                            {enModoEdicion ? (
                              <div className="table-cell-edit-actions">
                                <input
                                  type="text"
                                  className="table-cell-input"
                                  value={editable?.observaciones || ''}
                                  onChange={(e) => handleEditableFieldChange(movimientoKey, 'observaciones', e.target.value)}
                                  placeholder="Observaciones"
                                />
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button
                                    type="button"
                                    className="button btn-primary"
                                    style={{ padding: '6px 10px', fontSize: '12px' }}
                                    onClick={() => handleGuardarMovimientoEditado(mov, index)}
                                    disabled={guardandoMovimientoId === movimientoKey}
                                  >
                                    {guardandoMovimientoId === movimientoKey ? 'Guardando...' : 'Guardar'}
                                  </button>
                                  <button
                                    type="button"
                                    className="button"
                                    style={{ padding: '6px 10px', fontSize: '12px' }}
                                    onClick={() => handleResetMovimientoEditable(mov, index)}
                                    disabled={guardandoMovimientoId === movimientoKey}
                                  >
                                    Restaurar
                                  </button>
                                </div>
                              </div>
                            ) : mov.observaciones ? (
                              <span style={{ display: 'block', width: '100%' }}>{mov.observaciones}</span>
                            ) : (
                              <span style={{ color: '#adb5bd' }}>-</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Paginación */}
                  {pagination.total_pages > 1 && (
                    <div className="pagination">
                      <button
                        className="button"
                        onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                        disabled={pagination.page === 1}
                        style={{ marginRight: '10px' }}
                      >
                        Anterior
                      </button>
                      <span style={{ margin: '0 15px', alignSelf: 'center' }}>
                        Página {pagination.page} de {pagination.total_pages} ({pagination.total} total)
                      </span>
                      <button
                        className="button"
                        onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                        disabled={pagination.page >= pagination.total_pages}
                      >
                        Siguiente
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Tab: Nuevo Movimiento */}
        {activeTab === 'nuevo' && (
          <div className="tab-content-inner tab-content-form">
            <div className="tab-header">
              <div className="tab-controls">
                <div className="filters-row" style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'nowrap', width: '100%' }}>
                  <h2 style={{ margin: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>Nuevo Movimiento de Inventario</h2>
                  <CustomDropdown
                    className="custom-dropdown-toggle"
                    value={fincaFiltro}
                    placeholder={placeholderFincaFiltro}
                    options={opcionesFiltroFinca}
                    isOpen={dropdownFincaActivo === 'nuevo'}
                    onToggle={() => setDropdownFincaActivo((prev) => prev === 'nuevo' ? null : 'nuevo')}
                    onSelect={(value) => {
                      setFincaFiltro(value);
                      setHierroSeleccionado('');
                      setNuevoMovimiento({ ...nuevoMovimiento, hierro: '' });
                      setDropdownFincaActivo(null);
                    }}
                    containerRef={dropdownFincaRef}
                  />
                  {fincaFiltro && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'nowrap', flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => {
                          setHierroSeleccionado('');
                          setNuevoMovimiento({ ...nuevoMovimiento, hierro: '' });
                        }}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: hierroSeleccionado === '' ? '2px solid #4caf50' : '1px solid #ddd',
                          backgroundColor: hierroSeleccionado === '' ? '#f0f8f0' : 'white',
                          cursor: 'pointer',
                          fontWeight: hierroSeleccionado === '' ? 'bold' : 'normal',
                          transition: 'all 0.2s',
                          whiteSpace: 'nowrap',
                          flexShrink: 0
                        }}
                        title="Ver todos los hierros"
                      >
                        Todos
                      </button>
                      <div
                        className="hierros-container-primera-linea"
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(5, 70px)',
                          gap: '10px',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          flex: '1 1 auto',
                          minWidth: 0
                        }}
                      >
                        {hierros.length === 0 ? (
                          <span style={{ color: '#999', fontSize: '14px', whiteSpace: 'nowrap' }}>No hay hierros disponibles</span>
                        ) : (
                          hierros.map((hierro) => (
                            <button
                              key={hierro._id}
                              type="button"
                              onClick={() => {
                                setHierroSeleccionado(hierro._id);
                                setNuevoMovimiento({ ...nuevoMovimiento, hierro: hierro._id });
                              }}
                              style={{
                                width: '70px',
                                height: '70px',
                                padding: '4px',
                                border: hierroSeleccionado === hierro._id ? '3px solid #4caf50' : '2px solid #ddd',
                                borderRadius: '6px',
                                backgroundColor: hierroSeleccionado === hierro._id ? '#f0f8f0' : 'white',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                transition: 'all 0.2s',
                                boxShadow: hierroSeleccionado === hierro._id ? '0 2px 4px rgba(76, 175, 80, 0.3)' : '0 1px 2px rgba(0,0,0,0.1)',
                                flexShrink: 0
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.border = '3px solid #4caf50';
                                e.currentTarget.style.boxShadow = '0 2px 6px rgba(76, 175, 80, 0.4)';
                                e.currentTarget.style.transform = 'scale(1.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.border = hierroSeleccionado === hierro._id ? '3px solid #4caf50' : '2px solid #ddd';
                                e.currentTarget.style.boxShadow = hierroSeleccionado === hierro._id ? '0 2px 4px rgba(76, 175, 80, 0.3)' : '0 1px 2px rgba(0,0,0,0.1)';
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                              title={hierro.nombre}
                            >
                              {getImagenUrl(hierro.imagen_url, hierro.imagen) ? (
                                <img
                                  src={getImagenUrl(hierro.imagen_url, hierro.imagen) || ''}
                                  alt={hierro.nombre}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                    pointerEvents: 'none'
                                  }}
                                  onError={(e) => {
                                    // Si la imagen falla al cargar, ocultarla
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <span style={{ fontSize: '24px', pointerEvents: 'none' }}>🏷️</span>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="form-wrapper">
              <form onSubmit={handleCrearMovimiento} className="form">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Libro *</label>
                  <div className="libro-dropdown" ref={libroDropdownRef}>
                    <button
                      type="button"
                      className={`libro-dropdown-toggle ${mostrarDropdownLibro ? 'active' : ''}`}
                      onClick={() => setMostrarDropdownLibro((prev) => !prev)}
                      disabled={!fincaFiltro || loadingLibros}
                    >
                      <span>
                        {!fincaFiltro
                          ? 'Seleccione una finca primero...'
                          : loadingLibros
                            ? 'Cargando libros...'
                            : nuevoMovimiento.libro_id
                              ? (librosInventario.find((libro) => libro.libro_id === nuevoMovimiento.libro_id)?.nombre || 'Seleccionar libro...')
                              : 'Seleccionar libro...'}
                      </span>
                      <svg className="libro-dropdown-arrow" viewBox="0 0 1024 1024">
                        <path d="M426 726v-428l214 214z"></path>
                      </svg>
                    </button>

                    {mostrarDropdownLibro && fincaFiltro && !loadingLibros && (
                      <div className="libro-dropdown-menu">
                        {librosInventario.length === 0 ? (
                          <div className="libro-dropdown-empty">No hay libros creados para esta finca</div>
                        ) : (
                          librosInventario.map((libro) => (
                            <button
                              key={libro.libro_id}
                              type="button"
                              className={`libro-dropdown-item ${nuevoMovimiento.libro_id === libro.libro_id ? 'active' : ''}`}
                              onClick={() => {
                                setNuevoMovimiento({ ...nuevoMovimiento, libro_id: libro.libro_id });
                                setMostrarDropdownLibro(false);
                              }}
                            >
                              {libro.nombre}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="button"
                    onClick={() => setMostrarNuevoLibro((prev) => !prev)}
                    style={{ marginTop: '8px', padding: '6px 10px', fontSize: '13px' }}
                  >
                    + Nuevo libro
                  </button>
                  {mostrarNuevoLibro && (
                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexDirection: 'column' }}>
                      {!fincaFiltro && (
                        <small style={{ color: '#b26a00' }}>
                          Selecciona una finca para poder guardar el nuevo libro.
                        </small>
                      )}
                      <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Ingrese el nombre del libro"
                        value={nuevoLibroNombre}
                        onChange={(e) => setNuevoLibroNombre(e.target.value)}
                      />
                      <button
                        type="button"
                        className="button btn-primary"
                        onClick={handleCrearLibro}
                        disabled={guardandoLibro || !nuevoLibroNombre.trim() || !fincaFiltro}
                      >
                        {guardandoLibro ? 'Guardando...' : 'Guardar'}
                      </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Hoja *</label>
                  <input
                    type="number"
                    className="form-input"
                    value={nuevoMovimiento.hoja}
                    onChange={(e) => setNuevoMovimiento({ ...nuevoMovimiento, hoja: e.target.value })}
                    min="1"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Fecha *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={nuevoMovimiento.fecha}
                    onChange={(e) => setNuevoMovimiento({ ...nuevoMovimiento, fecha: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Hierro *</label>
                  <select
                    className="form-select"
                    value={nuevoMovimiento.hierro}
                    onChange={(e) => {
                      const hierroId = e.target.value;
                      setNuevoMovimiento({ ...nuevoMovimiento, hierro: hierroId });
                      setHierroSeleccionado(hierroId);
                    }}
                    required
                  >
                    <option value="">Seleccionar hierro...</option>
                    {hierros.length === 0 ? (
                      <option value="" disabled>
                        {fincaFiltro ? 'No hay hierros disponibles para la finca seleccionada' : 'No hay hierros disponibles'}
                      </option>
                    ) : (
                      hierros.map((hierro) => (
                      <option key={hierro._id} value={hierro._id}>
                          {hierro.nombre}{fincaFiltro ? ` - ${getFincasNombres(hierro)}` : ` (${getFincasNombres(hierro)})`}
                      </option>
                      ))
                    )}
                  </select>
                  {nuevoMovimiento.hierro && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#4caf50', fontWeight: '600' }}>
                      ✓ Hierro seleccionado: {hierros.find(h => h._id === nuevoMovimiento.hierro)?.nombre || 'Cargando...'}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group form-group-full">
                  <label className="form-label">Categoría *</label>
                <div className="categorias-grid">
                  {categorias.length === 0 ? (
                    <div className="empty-state">Cargando categorías...</div>
                  ) : (
                    categorias.map((cat) => {
                      const seleccionada = categoriasSeleccionadas.find(c => c.codigo === cat.codigo);
                      return (
                        <div
                          key={cat.codigo}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleCategoriaClick(cat)}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter' && e.key !== ' ') return;
                            const el = e.target as HTMLElement;
                            if (el.tagName === 'TEXTAREA' || el.tagName === 'BUTTON') return;
                            e.preventDefault();
                            handleCategoriaClick(cat);
                          }}
                          className={`categoria-select-button ${seleccionada ? 'selected' : ''}`}
                        >
                          <span className="categoria-badge">{cat.codigo}</span>
                          <span className="categoria-nombre">{cat.nombre}</span>
                          {seleccionada && (
                            <div className="categoria-seleccion-info">
                              <span className="categoria-tipo-movimiento">{seleccionada.tipo_movimiento_descripcion}</span>
                              <span className="categoria-cantidad">{seleccionada.cantidad}</span>
                              {INVENTARIO_FINCA_PERMITE_PESO && seleccionada.peso !== undefined && (
                                <span className="categoria-peso">{seleccionada.peso} kg</span>
                              )}
                              {INVENTARIO_FINCA_PERMITE_VALOR && (
                                <span className="categoria-valor">
                                  {seleccionada.valor !== undefined ? `$ ${seleccionada.valor.toLocaleString('es-CO')}` : 'Sin valor'}
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleObservacionCategoria(cat.codigo);
                                }}
                                className="categoria-observacion-btn"
                                title="Agregar observación para esta categoría"
                              >
                                Observación
                              </button>
                              {seleccionada.mostrarObservacion && (
                                <textarea
                                  className="form-textarea categoria-observacion-input"
                                  value={seleccionada.observaciones || ''}
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => e.stopPropagation()}
                                  onChange={(e) => handleObservacionCategoriaChange(cat.codigo, e.target.value)}
                                  rows={3}
                                  placeholder="Escribe observación para esta categoría..."
                                />
                              )}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEliminarCategoriaSeleccionada(cat.codigo);
                                }}
                                className="categoria-eliminar-btn"
                                title="Eliminar selección"
                              >
                                ×
                              </button>
                </div>
                          )}
                        </div>
                      );
                    })
                  )}
              </div>
                {categoriasSeleccionadas.length > 0 && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#4caf50', fontWeight: '600' }}>
                    ✓ {categoriasSeleccionadas.length} categoría(s) seleccionada(s)
                </div>
                )}
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-boton4 btn-save" disabled={loading}>
                  {loading ? 'Guardando...' : 'Crear Movimiento'}
                </button>
                <button
                  type="button"
                  className="refresh-button"
                  onClick={() => {
                    window.location.reload();
                  }}
                  style={{ marginLeft: '10px' }}
                  title="Recargar página"
                >
                  🔄
                </button>
              </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Modal para crear nuevo hierro */}
      {mostrarModalHierro && (
        <div 
          className="modal-overlay" 
          onClick={() => setMostrarModalHierro(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            overflow: 'auto'
          }}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              padding: '30px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: 'var(--dl-color-ganando-botones)' }}>Nuevo Hierro</h2>
              <button
                onClick={() => setMostrarModalHierro(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCrearHierro} className="form">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Nombre del Hierro *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={nuevoHierro.nombre}
                    onChange={(e) => setNuevoHierro({ ...nuevoHierro, nombre: e.target.value.toUpperCase() })}
                    required
                    placeholder="Ej: VILLA LIZA"
                  />
                </div>
              </div>

              <div className="form-group form-group-full">
                <label className="form-label">Fincas * (Seleccione una o más fincas)</label>
                  <div 
                    style={{ 
                      maxHeight: '200px', 
                      overflowY: 'auto', 
                      border: '1px solid #ddd', 
                      borderRadius: '6px', 
                      padding: '10px',
                      backgroundColor: '#f9f9f9'
                    }}
                  >
                    {fincas.length === 0 ? (
                      <p style={{ color: '#666', fontSize: '14px' }}>Cargando fincas...</p>
                    ) : (
                      fincas.map((finca) => (
                        <label
                          key={finca._id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            marginBottom: '5px',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#e8f5e9';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={nuevoHierro.finca_ids.includes(finca._id)}
                            onChange={() => handleFincaToggle(finca._id)}
                            style={{
                              marginRight: '10px',
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer'
                            }}
                          />
                          <span style={{ fontSize: '14px', color: '#333' }}>
                            {finca.nombre} ({finca.codigo})
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                  {nuevoHierro.finca_ids.length === 0 && (
                    <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                      * Debe seleccionar al menos una finca
                    </p>
                  )}
                  {nuevoHierro.finca_ids.length > 0 && (
                    <p style={{ color: '#4caf50', fontSize: '12px', marginTop: '5px' }}>
                      {nuevoHierro.finca_ids.length} finca(s) seleccionada(s)
                    </p>
                  )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Dueño *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={nuevoHierro.dueno}
                    onChange={(e) => setNuevoHierro({ ...nuevoHierro, dueno: e.target.value })}
                    required
                    placeholder="Nombre del dueño"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Número de Documento *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={nuevoHierro.numero_documento}
                    onChange={(e) => setNuevoHierro({ ...nuevoHierro, numero_documento: e.target.value })}
                    required
                    placeholder="Número de documento"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Fecha de Ingreso *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={nuevoHierro.fecha_ingreso}
                    onChange={(e) => setNuevoHierro({ ...nuevoHierro, fecha_ingreso: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group form-group-full">
                <label className="form-label">Descripción</label>
                <textarea
                  className="form-textarea"
                  value={nuevoHierro.descripcion}
                  onChange={(e) => setNuevoHierro({ ...nuevoHierro, descripcion: e.target.value })}
                  rows={4}
                  placeholder="Descripción del hierro (opcional)"
                />
              </div>

              <div className="form-group form-group-full">
                <label className="form-label">Imagen del Hierro</label>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif"
                  onChange={handleImagenChange}
                  className="form-input"
                  style={{ padding: '8px' }}
                />
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  Formatos permitidos: PNG, JPG, JPEG, GIF. Tamaño máximo: 5MB
                </p>
                {previewImagen && (
                  <div style={{ marginTop: '15px' }}>
                    <p style={{ fontSize: '14px', marginBottom: '8px', fontWeight: '600' }}>Vista previa:</p>
                    <img
                      src={previewImagen}
                      alt="Preview"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        padding: '5px',
                        objectFit: 'contain'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImagenHierro(null);
                        setPreviewImagen(null);
                      }}
                      style={{
                        marginLeft: '10px',
                        padding: '5px 10px',
                        background: '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Eliminar imagen
                    </button>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="button btn-primary" disabled={loading}>
                  {loading ? 'Guardando...' : 'Crear Hierro'}
                </button>
                <button
                  type="button"
                  className="button"
                  onClick={() => {
                    setMostrarModalHierro(false);
                    setNuevoHierro({
                      nombre: '',
                      finca_ids: [],
                      dueno: '',
                      numero_documento: '',
                      fecha_ingreso: new Date().toISOString().split('T')[0],
                      descripcion: ''
                    });
                    setImagenHierro(null);
                    setPreviewImagen(null);
                    setError(null);
                    setSuccess(null);
                  }}
                  style={{ marginLeft: '10px', background: '#6c757d', color: 'white' }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para seleccionar tipo de movimiento */}
      {mostrarModalTipoMovimiento && categoriaSeleccionadaModal && (
        <div 
          className="modal-overlay" 
          onClick={() => {
            setMostrarModalTipoMovimiento(false);
            setCategoriaSeleccionadaModal(null);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            overflow: 'auto'
          }}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '700px',
              maxHeight: '95vh',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Marca de agua del logo */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '80%',
              height: '80%',
              backgroundImage: 'url(/logocabeza.svg)',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: 'contain',
              opacity: 0.08,
              pointerEvents: 'none',
              zIndex: 0
            }} />
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', position: 'relative', zIndex: 1, flexShrink: 0 }}>
              <h2 style={{ margin: 0, color: 'var(--dl-color-ganando-botones)', fontSize: '20px' }}>
                Seleccionar Tipo de Movimiento - {categoriaSeleccionadaModal.nombre}
              </h2>
              <button
                onClick={() => {
                  setMostrarModalTipoMovimiento(false);
                  setCategoriaSeleccionadaModal(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <div className="tipos-movimiento-grid" style={{ position: 'relative', zIndex: 1, maxHeight: 'none', overflowY: 'visible', flex: 1, minHeight: 0, padding: '5px' }}>
              {tiposMovimiento.length === 0 ? (
                <div className="empty-state">Cargando tipos de movimiento...</div>
              ) : (
                tiposMovimiento
                  .filter((tipo) => {
                    if (ocultarEnSelectorTipoMovimiento(tipo.tipo)) return false;
                    if (tipo.descripcion.includes('Reclasificación interna')) return false;
                    return true;
                  })
                  .map((tipo) => (
                    <button
                      key={tipo.tipo}
                      type="button"
                      onClick={() => handleTipoMovimientoClick(tipo)}
                      className="tipo-movimiento-select-button"
                      style={{
                        border: `2px solid ${getEfectoColor(tipo.efecto)}`,
                        color: getEfectoColor(tipo.efecto)
                      }}
                    >
                      <span style={{ fontSize: '24px', marginRight: '10px' }}>
                        {getEfectoIcono(tipo.efecto)}
                      </span>
                      <div style={{ flex: 1, textAlign: 'left' }}>
                        <div style={{ fontWeight: '600', fontSize: '16px' }}>{tipo.descripcion}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {subtituloEfectoTipoMovimiento(tipo.efecto)}
                        </div>
                      </div>
                    </button>
                  ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal para ingresar cantidad */}
      {mostrarModalCantidad && categoriaSeleccionadaModal && tipoMovimientoSeleccionadoModal && (
        <div 
          className="modal-overlay" 
          onClick={() => {
            setMostrarModalCantidad(false);
            setCategoriaSeleccionadaModal(null);
            setTipoMovimientoSeleccionadoModal(null);
            setCantidadTemporal('');
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1002,
            overflow: 'auto'
          }}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              padding: '30px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '500px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Marca de agua del logo */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '80%',
              height: '80%',
              backgroundImage: 'url(/logocabeza.svg)',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: 'contain',
              opacity: 0.08,
              pointerEvents: 'none',
              zIndex: 0
            }} />
            <div style={{ 
              position: 'relative', 
              zIndex: 1, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '20px' 
            }}>
              <h2 style={{ margin: 0, color: 'var(--dl-color-ganando-botones)' }}>Ingresar Cantidad</h2>
              <button
                onClick={() => {
                  setMostrarModalCantidad(false);
                  setCategoriaSeleccionadaModal(null);
                  setTipoMovimientoSeleccionadoModal(null);
                  setCantidadTemporal('');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px', position: 'relative', zIndex: 1 }}>
              <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                <strong>Categoría:</strong> {categoriaSeleccionadaModal.nombre}
              </div>
              <div style={{ marginBottom: '20px', fontSize: '14px', color: '#666' }}>
                <strong>Tipo de Movimiento:</strong> {tipoMovimientoSeleccionadoModal.descripcion}
                <span style={{ 
                  marginLeft: '10px', 
                  color: getEfectoColor(tipoMovimientoSeleccionadoModal.efecto),
                  fontWeight: '600'
                }}>
                  {getEfectoIcono(tipoMovimientoSeleccionadoModal.efecto)}
                </span>
              </div>
            </div>

            <div className="form-group" style={{ position: 'relative', zIndex: 1 }}>
              <label className="form-label">Cantidad *</label>
              <input
                type="number"
                className="form-input"
                value={cantidadTemporal}
                onChange={(e) => setCantidadTemporal(e.target.value)}
                min="1"
                required
                autoFocus
                placeholder="Ingrese la cantidad"
                style={{ fontSize: '18px', padding: '12px', textAlign: 'center' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '30px', position: 'relative', zIndex: 1 }}>
              <button
                type="button"
                className="button btn-primary"
                onClick={handleConfirmarCantidad}
                style={{ flex: 1, padding: '12px' }}
              >
                Aceptar
              </button>
              <button
                type="button"
                className="button"
                onClick={() => {
                  setMostrarModalCantidad(false);
                  setCategoriaSeleccionadaModal(null);
                  setTipoMovimientoSeleccionadoModal(null);
                  setCantidadTemporal('');
                }}
                style={{ flex: 1, padding: '12px', background: '#6c757d', color: 'white' }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para ingresar peso */}
      {INVENTARIO_FINCA_PERMITE_PESO && mostrarModalPeso && categoriaSeleccionadaModal && tipoMovimientoSeleccionadoModal && cantidadTemporal && (
        <div 
          className="modal-overlay" 
          onClick={() => {
            setMostrarModalPeso(false);
            setMostrarModalValor(false);
            setCategoriaSeleccionadaModal(null);
            setTipoMovimientoSeleccionadoModal(null);
            setCantidadTemporal('');
            setPesoTemporal('');
            setValorTemporal('');
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1003,
            overflow: 'auto'
          }}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              padding: '30px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '500px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Marca de agua del logo */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '80%',
              height: '80%',
              backgroundImage: 'url(/logocabeza.svg)',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: 'contain',
              opacity: 0.08,
              pointerEvents: 'none',
              zIndex: 0
            }} />
            <div style={{ 
              position: 'relative', 
              zIndex: 1, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '20px' 
            }}>
              <h2 style={{ margin: 0, color: 'var(--dl-color-ganando-botones)' }}>Ingresar Peso</h2>
              <button
                onClick={() => {
                  setMostrarModalPeso(false);
                  setMostrarModalValor(false);
                  setCategoriaSeleccionadaModal(null);
                  setTipoMovimientoSeleccionadoModal(null);
                  setCantidadTemporal('');
                  setPesoTemporal('');
                  setValorTemporal('');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px', position: 'relative', zIndex: 1 }}>
              <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                <strong>Categoría:</strong> {categoriaSeleccionadaModal.nombre}
              </div>
              <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                <strong>Tipo de Movimiento:</strong> {tipoMovimientoSeleccionadoModal.descripcion}
                <span style={{ 
                  marginLeft: '10px', 
                  color: getEfectoColor(tipoMovimientoSeleccionadoModal.efecto),
                  fontWeight: '600'
                }}>
                  {getEfectoIcono(tipoMovimientoSeleccionadoModal.efecto)}
                </span>
              </div>
              <div style={{ marginBottom: '20px', fontSize: '14px', color: '#666' }}>
                <strong>Cantidad:</strong> {cantidadTemporal}
              </div>
            </div>

            <div className="form-group" style={{ position: 'relative', zIndex: 1 }}>
              <label className="form-label">Peso (kg) *</label>
              <input
                type="number"
                className="form-input"
                value={pesoTemporal}
                onChange={(e) => setPesoTemporal(e.target.value)}
                min="0"
                step="0.01"
                required
                autoFocus
                placeholder="Ingrese el peso en kilogramos"
                style={{ fontSize: '18px', padding: '12px', textAlign: 'center' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '30px', position: 'relative', zIndex: 1 }}>
              <button
                type="button"
                className="button btn-primary"
                onClick={handleConfirmarPeso}
                style={{ flex: 1, padding: '12px' }}
              >
                Aceptar
              </button>
              <button
                type="button"
                className="button"
                onClick={() => {
                  setMostrarModalPeso(false);
                  setMostrarModalValor(false);
                  setCategoriaSeleccionadaModal(null);
                  setTipoMovimientoSeleccionadoModal(null);
                  setCantidadTemporal('');
                  setPesoTemporal('');
                  setValorTemporal('');
                }}
                style={{ flex: 1, padding: '12px', background: '#6c757d', color: 'white' }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para ingresar valor */}
      {INVENTARIO_FINCA_PERMITE_VALOR && mostrarModalValor && categoriaSeleccionadaModal && tipoMovimientoSeleccionadoModal && cantidadTemporal && pesoTemporal && (
        <div 
          className="modal-overlay" 
          onClick={() => {
            setMostrarModalValor(false);
            setCategoriaSeleccionadaModal(null);
            setTipoMovimientoSeleccionadoModal(null);
            setCantidadTemporal('');
            setPesoTemporal('');
            setValorTemporal('');
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1004,
            overflow: 'auto'
          }}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              padding: '30px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '500px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Marca de agua del logo */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '80%',
              height: '80%',
              backgroundImage: 'url(/logocabeza.svg)',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: 'contain',
              opacity: 0.08,
              pointerEvents: 'none',
              zIndex: 0
            }} />
            <div style={{ 
              position: 'relative', 
              zIndex: 1, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '20px' 
            }}>
              <h2 style={{ margin: 0, color: 'var(--dl-color-ganando-botones)' }}>Ingresar Valor</h2>
              <button
                onClick={() => {
                  setMostrarModalValor(false);
                  setCategoriaSeleccionadaModal(null);
                  setTipoMovimientoSeleccionadoModal(null);
                  setCantidadTemporal('');
                  setPesoTemporal('');
                  setValorTemporal('');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px', position: 'relative', zIndex: 1 }}>
              <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                <strong>Categoría:</strong> {categoriaSeleccionadaModal.nombre}
              </div>
              <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                <strong>Tipo de Movimiento:</strong> {tipoMovimientoSeleccionadoModal.descripcion}
                <span style={{ 
                  marginLeft: '10px', 
                  color: getEfectoColor(tipoMovimientoSeleccionadoModal.efecto),
                  fontWeight: '600'
                }}>
                  {getEfectoIcono(tipoMovimientoSeleccionadoModal.efecto)}
                </span>
              </div>
              <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                <strong>Cantidad:</strong> {cantidadTemporal}
              </div>
              <div style={{ marginBottom: '20px', fontSize: '14px', color: '#666' }}>
                <strong>Peso:</strong> {pesoTemporal} kg
              </div>
            </div>

            <div className="form-group" style={{ position: 'relative', zIndex: 1 }}>
              <label className="form-label">Valor (COP) *</label>
              <input
                type="text"
                className="form-input"
                value={valorTemporal ? formatearPesos(valorTemporal) : ''}
                onChange={(e) => {
                  const numeros = extraerNumeros(e.target.value);
                  setValorTemporal(numeros);
                }}
                required
                autoFocus
                placeholder="Ingrese el valor en pesos"
                style={{ fontSize: '18px', padding: '12px', textAlign: 'center', letterSpacing: '0.5px' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '30px', position: 'relative', zIndex: 1 }}>
              <button
                type="button"
                className="button btn-primary"
                onClick={handleConfirmarValor}
                style={{ flex: 1, padding: '12px' }}
              >
                Aceptar
              </button>
              <button
                type="button"
                className="button"
                onClick={() => {
                  setMostrarModalValor(false);
                  setCategoriaSeleccionadaModal(null);
                  setTipoMovimientoSeleccionadoModal(null);
                  setCantidadTemporal('');
                  setPesoTemporal('');
                  setValorTemporal('');
                }}
                style={{ flex: 1, padding: '12px', background: '#6c757d', color: 'white' }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para seleccionar categoría destino en reclasificación */}
      {mostrarModalCategoriaDestino && categoriaSeleccionadaModal && tipoMovimientoSeleccionadoModal && cantidadTemporal && (!INVENTARIO_FINCA_PERMITE_PESO || pesoTemporal) && (!INVENTARIO_FINCA_PERMITE_VALOR || (valorTemporal !== null && valorTemporal !== undefined && valorTemporal !== '')) && (
        <div 
          className="modal-overlay" 
          onClick={() => {
            setMostrarModalCategoriaDestino(false);
            setCategoriaDestinoReclasificacion(null);
            setCategoriaSeleccionadaModal(null);
            setTipoMovimientoSeleccionadoModal(null);
            setCantidadTemporal('');
            setPesoTemporal('');
            setValorTemporal('');
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1005,
            overflow: 'auto'
          }}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '700px',
              maxHeight: '90vh',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* Marca de agua del logo */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '80%',
              height: '80%',
              backgroundImage: 'url(/logocabeza.svg)',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: 'contain',
              opacity: 0.08,
              pointerEvents: 'none',
              zIndex: 0
            }} />
            
            <div style={{
              padding: '30px',
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
              position: 'relative',
              zIndex: 1
            }}>
            
            <div style={{ 
              position: 'relative', 
              zIndex: 1, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '20px' 
            }}>
              <h2 style={{ margin: 0, color: 'var(--dl-color-ganando-botones)' }}>
                Seleccionar Categoría Destino - Reclasificación
              </h2>
              <button
                onClick={() => {
                  setMostrarModalCategoriaDestino(false);
                  setCategoriaDestinoReclasificacion(null);
                  setCategoriaSeleccionadaModal(null);
                  setTipoMovimientoSeleccionadoModal(null);
                  setCantidadTemporal('');
                  setPesoTemporal('');
                  setValorTemporal('');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px', position: 'relative', zIndex: 1 }}>
              <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                <strong>Categoría Origen:</strong> {categoriaSeleccionadaModal.nombre} ({categoriaSeleccionadaModal.codigo})
              </div>
              <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                <strong>Cantidad:</strong> {cantidadTemporal} animales
              </div>
              {INVENTARIO_FINCA_PERMITE_PESO && (
                <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                  <strong>Peso:</strong> {pesoTemporal} kg
                </div>
              )}
              {INVENTARIO_FINCA_PERMITE_VALOR && (
                <div style={{ marginBottom: '20px', fontSize: '14px', color: '#666' }}>
                  <strong>Valor:</strong> $ {parseFloat(valorTemporal).toLocaleString('es-CO')}
                </div>
              )}
            </div>

            <div style={{ position: 'relative', zIndex: 1 }}>
              <label className="form-label" style={{ marginBottom: '15px', display: 'block', fontWeight: '600' }}>
                Seleccione la categoría destino *
              </label>
              <div className="categorias-grid" style={{ overflowY: 'visible' }}>
                {categorias.length === 0 ? (
                  <div className="empty-state">Cargando categorías...</div>
                ) : (
                  categorias
                    .filter((cat) => cat.codigo !== categoriaSeleccionadaModal.codigo) // Excluir la categoría actual
                    .map((cat) => (
                      <button
                        key={cat.codigo}
                        type="button"
                        onClick={() => setCategoriaDestinoReclasificacion(cat)}
                        className={`categoria-select-button ${categoriaDestinoReclasificacion?.codigo === cat.codigo ? 'selected' : ''}`}
                        style={{
                          border: categoriaDestinoReclasificacion?.codigo === cat.codigo 
                            ? '2px solid #2196f3' 
                            : '2px solid #ddd',
                          backgroundColor: categoriaDestinoReclasificacion?.codigo === cat.codigo 
                            ? '#e3f2fd' 
                            : 'white'
                        }}
                      >
                        <span className="categoria-badge">{cat.codigo}</span>
                        <span className="categoria-nombre">{cat.nombre}</span>
                      </button>
                    ))
                )}
              </div>
            </div>

            {error && (
              <div style={{ 
                marginTop: '15px', 
                padding: '10px', 
                backgroundColor: '#ffebee', 
                color: '#c62828', 
                borderRadius: '4px',
                position: 'relative',
                zIndex: 1
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '30px', position: 'relative', zIndex: 1, paddingBottom: '10px' }}>
              <button
                type="button"
                className="button btn-primary"
                onClick={() => {
                  if (categoriaDestinoReclasificacion) {
                    handleConfirmarReclasificacion();
                  } else {
                    setError('Por favor seleccione una categoría destino');
                  }
                }}
                style={{ 
                  flex: 1, 
                  padding: '12px',
                  opacity: categoriaDestinoReclasificacion ? 1 : 0.5,
                  cursor: categoriaDestinoReclasificacion ? 'pointer' : 'not-allowed'
                }}
              >
                Confirmar Reclasificación
              </button>
              <button
                type="button"
                className="button"
                onClick={() => {
                  setMostrarModalCategoriaDestino(false);
                  setCategoriaDestinoReclasificacion(null);
                  setCategoriaSeleccionadaModal(null);
                  setTipoMovimientoSeleccionadoModal(null);
                  setCantidadTemporal('');
                  setPesoTemporal('');
                  setValorTemporal('');
                }}
                style={{ flex: 1, padding: '12px', background: '#6c757d', color: 'white' }}
              >
                Cancelar
              </button>
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default InventarioFinca
